import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  ALL,
  FACEBOOK,
  INSTAGRAM,
  PLATFORM_LIST,
  PLATFORM_LIST_NAMES,
  PLATFORM_LIST_WITH_ALL,
  TWITTER,
  WHATSAPP,
  YOUTUBE
} from '../../../config/constants';
import {
  POST_TYPE_KEY,
  POST_TYPE_MESSAGE,
  POST_TYPE_POST_KEY,
  POST_TYPE_REEL_KEY,
  POST_TYPE_TRIAL_REEL_KEY,
  POST_TYPE_SHORT_VIDEO_KEY,
  POST_TYPE_STATUS,
  POST_TYPE_STORY_KEY,
  POST_TYPE_TO_PREFIX,
  POST_TYPE_VIDEO_KEY,
  POST_TYPE_CHOICES_MAP,
  platformFeatures,
  POST_TYPE_CHOICE_POST,
  POST_TYPE_TEMPLATE,
  POST_TYPE_CHOICE_TEMPLATE
} from '../../../components/lomavisCreator/config/platformFeatures';
import { PLATFORM_STATIC_ICONS_PNG } from '../../../config/icons';
import logo from '../../../assets/images/lomavis/logo/lomavis-logo.png';
import twitterText from 'twitter-text';

import type {
  BestTimeToPostOverview,
  CampaignType
} from './lomavisCreatorConfig';
import { readLomavisCreatorConfig } from './lomavisCreatorConfig';
import {
  ACTIONS_VALUES_MAP,
  DEFUALT_INPUT_TIME,
  PREVIEW_TYPE_STANDARD,
  GRADUATION_STRATEGY_MANUAL
} from '../../../components/lomavisCreator/config/constants';
import axiosClient from '../../../utils/axios';
import { fromArrayStringToArr, fromArrayToArrString } from './utils';
import { extractTimeFromRecurrence } from '../../../components/lomavisCreator/utils/helpers';
import { resolve } from '../../../api/endPoints';
import moment from 'moment';

import { ex } from '@fullcalendar/core/internal-common';
import MediaAltTags from '../../../components/lomavisCreator/components/mediaAltTags';
import { Catalog } from '@/components/lomavisCreator/components/modals/InstagramProductsTags/productCatalogSelect';

export const MEDIA_UPLOAD = 'mediaUpload';
export const MEDIA_REORDERING = 'mediaReordering';

const savedLomavisCreatorConfig = readLomavisCreatorConfig();

const formatBestTimeToPostValue = (
  overview?: BestTimeToPostOverview | null
) => {
  const bestHour = overview?.best_hour;
  const bestMinute = overview?.best_minute ?? 0;

  return bestHour != null && bestMinute != null
    ? `${String(bestHour).padStart(2, '0')}:${String(bestMinute).padStart(
        2,
        '0'
      )}`
    : DEFUALT_INPUT_TIME;
};

const getBestTimeToPostOverviewForPlatform = (platform?: string | null) => {
  if (platform && platform !== ALL) {
    const platformOverview =
      savedLomavisCreatorConfig?.best_time_to_post?.platform_overview?.[
        platform
      ];

    if (platformOverview?.best_hour != null) {
      return platformOverview;
    }
  }

  return savedLomavisCreatorConfig?.best_time_to_post?.overview;
};

export const getDefaultBestTimeToPostValue = (platform?: string | null) =>
  formatBestTimeToPostValue(getBestTimeToPostOverviewForPlatform(platform));

export const bestTimeToPostValue = getDefaultBestTimeToPostValue(ALL);
const aiAssistantDefaultState = {
  profile: savedLomavisCreatorConfig?.lomavis_ai?.lomavis_ai_settings || {},
  aIQuota: savedLomavisCreatorConfig?.lomavis_ai?.lomavis_ai_quota || {},

  sessionID: savedLomavisCreatorConfig?.lomavis_ai?.lomavis_ai_session_uuid,
  conversation: {
    nextPage: null,
    isLoading: false,
    isInitiated: false,
    isWaitingForResponse: false,
    count: 0,
    messages: [],
    pendingMessages: [],
    currentMessage: '',
    messageSettings:
      savedLomavisCreatorConfig?.lomavis_ai?.lomavis_ai_settings || {},
    pendingUseText: null
  },
  ui: {
    showProfileSettings: false,
    showStartSettings: false,
    showQuickActions: false,
    showActionOptions: false,
    showUseTextConfirmation: false,
    showOwnIdentityModal: true,
    showMainActionUrlInput: false,
    focusMainTextField: null
  }
};

const postBoostDefaultState = {
  loading: false,
  ui: {
    sameBoostForAll: true
  },
  boost_config: {
    // Platform-specific configurations: { [platform]: BoostConfig }
  },
  configAlreadySaved: false
};

export const creatorInitialState = {
  uuid: '',
  quillEditors: {},
  aiAssistant: { ...aiAssistantDefaultState },
  postBoost: { ...postBoostDefaultState },
  created_in_lomavis: true,

  tempState: { creator_multipost_config: {} },
  multipost_use_instagram_default_location: false,
  postIdea: null,
  multipost_copied_to_profile_group_for_approval: false,
  post_status: null,
  post_sub_status: null,
  has_been_published: false,
  cropsCache: {},
  lomavisCreatorConfig: readLomavisCreatorConfig(),

  accessibility_guard:
    readLomavisCreatorConfig()?.features?.accessibility_guard || false,

  instagram_direct_connect:
    savedLomavisCreatorConfig?.additional_data?.instagram_direct_connect,

  persistedPlatformEnabled: {},

  platformEnabled: PLATFORM_LIST.reduce((acc, platform) => {
    acc[platform] =
      savedLomavisCreatorConfig?.supported_platforms[platform] &&
      savedLomavisCreatorConfig?.connected_platforms[platform];
    return acc;
  }, {}),
  locationUuid: savedLomavisCreatorConfig?.location?.uuid,
  cloudUuid: savedLomavisCreatorConfig?.cloud?.uuid,
  loadedMediaFiles: savedLomavisCreatorConfig?.load_media_files,
  browserExtensionData: savedLomavisCreatorConfig?.browser_extension_data,
  postCampaign: savedLomavisCreatorConfig?.features.post_campaigns,

  postTypeSelected: PLATFORM_LIST.reduce((acc, platform) => {
    acc[platform] = POST_TYPE_POST_KEY;
    if (platform === YOUTUBE) {
      acc[platform] = POST_TYPE_VIDEO_KEY;
    }
    if (platform === WHATSAPP) {
      acc[platform] = POST_TYPE_MESSAGE;
    }
    return acc;
  }, {}),

  postData: PLATFORM_LIST_WITH_ALL.reduce((postData, platform) => {
    postData[platform] = {
      // adding additional fields
      url_for_linkpreview: null,
      show_preview_url: true,
      first_comment: null,
      additional_publication_data: {},
      // load default values from lomavisCreatorConfig if given else set to null
      instagram_location_id:
        platform == INSTAGRAM
          ? savedLomavisCreatorConfig?.creator_defaults?.instagram_location_id
          : null,
      instagram_location_name:
        platform == INSTAGRAM
          ? savedLomavisCreatorConfig?.creator_defaults?.instagram_location_name
          : null,

      instagram_shopping_tags: platform == INSTAGRAM ? [] : null,

      facebook_location_name:
        platform == FACEBOOK
          ? savedLomavisCreatorConfig?.creator_defaults?.facebook_location_id
          : null,
      facebook_location_id:
        platform == FACEBOOK
          ? savedLomavisCreatorConfig?.creator_defaults?.facebook_location_id
          : null,
      is_facebook_reel: false,
      is_facebook_story: false,
      is_instagram_reel: false,
      is_instagram_story: false,
      instagram_user_tags: [],
      google_cta_type: null,
      google_cta_url: null,
      youtube_tags: [],
      youtube_title: null,
      youtube_category: null,
      youtube_privacy_status: null,
      youtube_available_for_kids: null,
      linkedin_document_title: null,
      media: [],
      cropList: [],
      unEditedMedia: [],
      mediaUploading: [],
      draggedMediaFiles: [],
      mediaLibraryFiles: [],
      text: { ops: [{ insert: '' }] },
      text_as_html: '',
      text_as_text: '',
      text_length: 0,
      text_length_striped: 0,
      plannedPublicationDate: null,
      recurrence: null,
      planned_publication_datetime_date: null,
      planned_publication_datetime_time: getDefaultBestTimeToPostValue(
        platform
      ),
      planned_publication_datetime: null,
      tempPublicationData: {
        date: null,
        time: getDefaultBestTimeToPostValue(platform),
        datetime: null
      },
      urlPreview: null,
      source_image: {
        uuid: ''
      },
      thumbnail_image: {
        uuid: ''
      },
      uploadedImageThumbnail: {
        url: '',
        file: null,
        uploadPresets: null,
        uploaded: false
      },
      editedImageThumbnail: {
        url: '',
        file: null,
        uploadPresets: null,
        uploaded: false,
        media: {}
      },
      fullEditorImage: {
        url: '',
        file: null,
        uploadPresets: null,
        uploaded: false,
        currentCrop: null
      },
      layeredImageThumbnail: {
        file: null,
        uploadPresets: null,
        uploaded: false,
        media: {}
      },
      video_thumbnail: {},
      video_thumbnail_editor: {},
      video_thumbnail_background_image: '',

      thumbnail_preview_processing: false
    };
    if (platform === INSTAGRAM) {
      postData[platform].instagram_user_tags = Array(10).fill([]);
      postData[platform].instagram_collaborators = [];
      postData[platform].instagram_temp_tags = [];

      postData[platform].additional_publication_data = {
        ...postData[platform].additional_publication_data,
        instagram_shopping_select_store: null,
        instagram_shopping_product_tags: {}
      };

      postData[platform].allowTagModalClose = false;
      postData[platform].allowProductTagModalClose = false;
    }
    return postData;
  }, {}),

  campaigns: [],
  mediaFiles: [],
  renderedTemplateImageBlob: [],
  overlayData: [],
  brandData: {
    primaryColor:
      // @ts-expect-error
      readLomavisCreatorConfig()?.template_branding_data?.primaryColor,

    secondaryColor:
      // @ts-expect-error
      readLomavisCreatorConfig()?.template_branding_data?.secondaryColor,

    logo:
      // @ts-expect-error
      readLomavisCreatorConfig()?.template_branding_data?.logo,

    fonts: {
      fontFamily:
        // @ts-ignore
        readLomavisCreatorConfig()?.template_branding_data?.fonts?.family,

      fontFaceUrl:
        //@ts-ignore
        readLomavisCreatorConfig()?.template_branding_data?.fontFaceUrl ||
        //@ts-ignore
        readLomavisCreatorConfig()?.template_branding_data?.fonts?.fontFaceUrl,

      // @ts-ignore
      bold: readLomavisCreatorConfig()?.template_branding_data?.fonts
        ?.customFontBold,

      normal:
        // @ts-expect-error
        readLomavisCreatorConfig()?.template_branding_data?.fonts
          ?.customFontNormal
    }
  },

  // @ts-expect-error
  overlay_enabled: readLomavisCreatorConfig()?.overlay_config?.enabled,

  createdOverlayArray: [],
  approval_process: null,
  assetUploading: false,
  recurrence_as_str: '',
  time: getDefaultBestTimeToPostValue(ALL),
  timeZone: savedLomavisCreatorConfig?.timezone,
  is_multipost: savedLomavisCreatorConfig?.is_multipost,
  // is_multipost: true,
  is_template_post: false,
  lomavisCreatorCacheReload: {},
  is_recurring_post: false,
  general_post_type: POST_TYPE_KEY,
  publication_delay_in_minutes: undefined,
  useSeparateMediaFiles: false,
  useSeparateTextFields: false,
  use_separate_publication_datetime: false,
  use_separate_publication_datetime_date: false,
  use_separate_publication_datetime_time: false,
  all_platforms_publication_datetime_date: null,
  all_platforms_publication_datetime_time: '',
  uploadedImageThumbnail: null,
  allowEditorClose: false,
  allowBrandEditorClose: false,
  allowOverlayCreatorClose: false,
  allowMultiImageEditor: false,
  allowThumbnailEditorClose: true,
  creator_multipost_config: {},
  lastCropUpdateTimestamp: null,
  selectionFilters: {
    byPlatform: [],
    byTag: []
  },

  ui: {
    showOnlySelectedPlatforms: false,
    showProfileGroupSelectModal: false,
    scheduleModalToggle: false,
    showEditImageModal: false,
    previewMode: PREVIEW_TYPE_STANDARD,
    showPostIdeaModal: false,
    uploadAreaVisible: true,
    showAiAssistant: false,
    showPostBoost: false
  },
  schedulePlanningData: {
    time: getDefaultBestTimeToPostValue(ALL),
    recurrence_as_str: '',
    is_recurring_post: false,
    publication_delay_in_minutes: undefined
  },
  publishingDelayData: {
    days: null,
    hours: null,
    minutes: null,
    totalMinutes: null,
    noPublishDays: [],
    noPublishStartTime: '',
    noPublishEndTime: ''
  },
  temporaryPublishingDelayData: {
    days: null,
    hours: null,
    minutes: null,
    totalMinutes: null,
    noPublishDays: [],
    noPublishStartTime: '',
    noPublishEndTime: ''
  },

  modalStates: {
    schedule: {
      toggle: false
    },
    thumbnail: {
      toggle: false,
      source: '',
      platform: ALL
    },
    brandOverlay: {
      toggle: false,
      source: '',
      platform: ALL
    },
    mediaEditor: {
      toggle: false,
      platform: ALL,
      currentIndex: null
    },
    mediaAltTags: {
      toggle: false,
      platform: ALL,
      currentIndex: null,
      allowClose: true
    },
    mediaViewer: {
      toggle: false,
      platform: ALL,
      currentIndex: null
    },
    browserPlugin: {
      toggle: false,
      allowClose: true
    },
    brandMediaEditor: {
      toggle: false,
      platform: ALL,
      currentIndex: null,
      cropMode: false
    },
    multiMediaEditor: {
      toggle: false,
      platform: ALL
    },
    IGTags: {
      toggle: false,
      platform: ALL
    },
    IGProductTags: {
      toggle: false,
      platform: ALL,
      allowClose: true
    },
    IGMusic: {
      toggle: false,
      platform: ALL
    }
  },
  uploadCancelToken: null
};

const lomavisCreatorSlice = createSlice({
  name: 'lomavisCreator',
  initialState: creatorInitialState,
  reducers: {
    updatePlatformEnabled: (
      state,
      action: PayloadAction<{ platform: string; enabled: boolean }>
    ) => {
      // check that at least one platform is active
      if (!action.payload.enabled) {
        const areAtLeastTwoPlatformsEnabled =
          PLATFORM_LIST.map(
            (platform) => state.platformEnabled[platform]
          ).filter((isEnabled) => isEnabled === true).length >= 2;

        if (!areAtLeastTwoPlatformsEnabled) {
          // Do not allow disabling all platforms
          return;
        }
      }
      // check if we currently enabling platform and use_separate_text_fields is enabled
      if (action.payload.enabled) {
        // move the from ALL to the new platform postData
        const allPostData = { ...state.postData[ALL] };

        // move text related fields
        if (!state.useSeparateTextFields) {
          state.postData[action.payload.platform].text = allPostData.text;
          state.postData[action.payload.platform].text_as_html =
            allPostData.text_as_html;
          state.postData[action.payload.platform].text_length =
            allPostData.text_length;
          state.postData[action.payload.platform].text_length_striped =
            allPostData.text_length_striped;
          // move url preview
          state.postData[action.payload.platform].urlPreview =
            allPostData.urlPreview;
          // move url for linkpreview
          state.postData[action.payload.platform].url_for_linkpreview =
            allPostData.url_for_linkpreview;
        }

        if (!state.useSeparateMediaFiles) {
          const curr = state.postData[action.payload.platform]?.cropList;

          state.postData[action.payload.platform].cropList = curr?.length
            ? curr
            : allPostData?.cropList;
          state.postData[action.payload.platform].media = allPostData.media;

          state.postData[action.payload.platform].unEditedMedia = [];
          state.postData[action.payload.platform].mediaUploading = [];
        }

        state.postData[action.payload.platform].first_comment = null;
        state.postData[action.payload.platform].instagram_location_id =
          allPostData.instagram_location_id;
        state.postData[action.payload.platform].instagram_location_name =
          allPostData.instagram_location_name;
        state.postData[action.payload.platform].facebook_location_name =
          allPostData.facebook_location_name;
        state.postData[action.payload.platform].facebook_location_id =
          allPostData.facebook_location_id;
        state.postData[action.payload.platform].is_facebook_reel =
          allPostData.is_facebook_reel;
        state.postData[action.payload.platform].is_facebook_story =
          allPostData.is_facebook_story;
        state.postData[action.payload.platform].is_instagram_reel =
          allPostData.is_instagram_reel;
        state.postData[action.payload.platform].is_instagram_story =
          allPostData.is_instagram_story;
        state.postData[action.payload.platform].instagram_user_tags =
          allPostData.instagram_user_tags;
        state.postData[action.payload.platform].google_cta_type =
          allPostData.google_cta_type;
        state.postData[action.payload.platform].google_cta_url =
          allPostData.google_cta_url;
        state.postData[action.payload.platform].youtube_tags =
          allPostData.youtube_tags;
        state.postData[action.payload.platform].youtube_title =
          allPostData.youtube_title;
        state.postData[action.payload.platform].youtube_category =
          allPostData.youtube_category;
        state.postData[action.payload.platform].youtube_privacy_status =
          allPostData.youtube_privacy_status;
        state.postData[action.payload.platform].youtube_available_for_kids =
          allPostData.youtube_available_for_kids;
        state.postData[action.payload.platform].linkedin_document_title =
          allPostData.linkedin_document_title;

        state.postData[action.payload.platform].plannedPublicationDate =
          allPostData.plannedPublicationDate;
        state.postData[action.payload.platform].recurrence =
          allPostData.recurrence;
        state.postData[action.payload.platform].planned_publication_datetime =
          allPostData.planned_publication_datetime;
        state.postData[
          action.payload.platform
        ].planned_publication_datetime_date =
          allPostData.planned_publication_datetime_date;
        state.postData[
          action.payload.platform
        ].planned_publication_datetime_time =
          allPostData.planned_publication_datetime_time;
        state.postData[action.payload.platform].tempPublicationData =
          allPostData.tempPublicationData;

        state.postData[action.payload.platform].source_image =
          allPostData.source_image;
        state.postData[action.payload.platform].thumbnail_image =
          allPostData.thumbnail_image;
        state.postData[action.payload.platform].uploadedImageThumbnail =
          allPostData.uploadedImageThumbnail;
        state.postData[action.payload.platform].editedImageThumbnail =
          allPostData.editedImageThumbnail;
        state.postData[action.payload.platform].fullEditorImage =
          allPostData.fullEditorImage;
        state.postData[action.payload.platform].layeredImageThumbnail =
          allPostData.layeredImageThumbnail;
        state.postData[action.payload.platform].video_thumbnail =
          allPostData.video_thumbnail;
        state.postData[action.payload.platform].video_thumbnail_editor =
          allPostData.video_thumbnail_editor;
        state.postData[
          action.payload.platform
        ].video_thumbnail_background_image =
          allPostData.video_thumbnail_background_image;
        state.postData[action.payload.platform].thumbnail_preview_processing =
          allPostData.thumbnail_preview_processing;
      }
      state.platformEnabled[action.payload.platform] = action.payload.enabled;
    },
    isPlatformEnabled: (state, action: PayloadAction<{ platform: string }>) => {
      return state.platformEnabled[action.payload.platform];
    },
    isPlatformSupported: (
      state,
      action: PayloadAction<{ platform: string }>
    ) => {
      return state.lomavisCreatorConfig.supported_platforms[
        action.payload.platform
      ];
    },
    updatePostTypeSelected: (
      state,
      action: PayloadAction<{ platform: string; postType: string }>
    ) => {
      state.postTypeSelected[action.payload.platform] = action.payload.postType;

      // set all affected fields to default values. Then activate the ones that are needed
      // this is needed to update the selected post type such as reel or story
      const fields_to_reset = []
        .concat(
          Object.keys(platformFeatures[action.payload.platform]).map((x) =>
            platformFeatures[action.payload.platform][x].map(
              (y) => y.dataFieldEnable
            )
          )
        )
        .flat()
        .filter(Boolean);

      for (const field of fields_to_reset) {
        state.postData[action.payload.platform][field] = false;
      }

      // Derive general_post_type based on postType
      const generalPostType = [
        POST_TYPE_REEL_KEY,
        POST_TYPE_TRIAL_REEL_KEY,
        POST_TYPE_STORY_KEY
      ].includes(action.payload.postType)
        ? POST_TYPE_KEY
        : state.general_post_type;

      // enable the field that are needed for the selected post type
      console.log(generalPostType, 'general post type');
      platformFeatures[action.payload.platform][generalPostType]
        .filter((x) => x.typeKey === action.payload.postType)
        .map((x) =>
          x.dataFieldEnable?.forEach((field) => {
            state.postData[action.payload.platform][field] = true;
          })
        );

      // Default graduation strategy for trial reels
      if (
        action.payload.platform === INSTAGRAM &&
        action.payload.postType === POST_TYPE_TRIAL_REEL_KEY
      ) {
        const current =
          state.postData[INSTAGRAM].additional_publication_data
            ?.instagram_graduation_strategy;
        state.postData[INSTAGRAM].additional_publication_data = {
          ...state.postData[INSTAGRAM].additional_publication_data,
          instagram_graduation_strategy:
            current || GRADUATION_STRATEGY_MANUAL,
          instagram_trial_reel_enabled: true
        };
      } else if (action.payload.platform === INSTAGRAM) {
        // Clear strategy when leaving trial reel so it loads as normal reel later
        if (
          state.postData[INSTAGRAM].additional_publication_data
            ?.instagram_graduation_strategy ||
          state.postData[INSTAGRAM].additional_publication_data
            ?.instagram_trial_reel_enabled
        ) {
          const {
            instagram_graduation_strategy,
            instagram_trial_reel_enabled,
            ...rest
          } = state.postData[INSTAGRAM].additional_publication_data;
          state.postData[INSTAGRAM].additional_publication_data = { ...rest };
        }
      }
    },
    getPostTypeSelected: (
      state,
      action: PayloadAction<{ platform: string }>
    ) => {
      return state.postTypeSelected[action.payload.platform];
    },
    updateUseSeparateMediaFiles: (state, action: PayloadAction<boolean>) => {
      state.useSeparateMediaFiles = action.payload;
    },
    updateUseSeparateTextFields: (state, action: PayloadAction<boolean>) => {
      state.useSeparateTextFields = action.payload;
    },
    updateUseSeparatePublicationDateTime: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.use_separate_publication_datetime = action.payload;
    },
    addMediaFiles: (
      state,
      action: PayloadAction<{ platform: string | null; files: any[] }>
    ) => {
      // TODO: Clear uploading media files when the upload is finished
      // Where will I handle the upload? In the component or in the slice?

      state.postData[action.payload.platform].media = [
        ...state.postData[action.payload.platform].media,
        ...action.payload.files
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p].media = [
              ...state.postData[p].media,
              ...action.payload.files
            ];
          }
        }
      }
    },

    addUnEditedMediaFiles: (
      state,
      action: PayloadAction<{ platform: string | null; files: any[] }>
    ) => {
      const existingMediaIds = new Set(
        state.postData[action.payload.platform].unEditedMedia.map(
          (file) => file.id
        )
      );

      // Filter out files that already exist
      const newFiles = action.payload.files.filter(
        (file) => !existingMediaIds.has(file.id)
      );

      state.postData[action.payload.platform].unEditedMedia = [
        ...state.postData[action.payload.platform].unEditedMedia,
        ...newFiles
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            const existingMediaIdsForPlatform = new Set(
              state.postData[p].unEditedMedia.map((file) => file.id)
            );

            const newFilesForPlatform = action.payload.files.filter(
              (file) => !existingMediaIdsForPlatform.has(file.id)
            );

            state.postData[p].unEditedMedia = [
              ...state.postData[p].unEditedMedia,
              ...newFilesForPlatform
            ];
          }
        }
      }
    },

    clearEditedMediafile: (
      state,
      action: PayloadAction<{ platform: string | null }>
    ) => {
      state.postData[action.payload.platform].unEditedMedia = [];

      state.postData[action.payload.platform].media.map((item) => {
        item.editorUpdateState = false;
      });

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].unEditedMedia = [];
          state.postData[p].media.map((item) => {
            item.editorUpdateState = false;
          });
        }
      }
    },
    deleteUnEditedMediafile: (
      state,
      action: PayloadAction<{ platform: string | null; fileID: any }>
    ) => {
      state.postData[action.payload.platform].unEditedMedia = [
        ...state.postData[action.payload.platform].unEditedMedia.filter(
          (obj) => obj.id !== action.payload.fileID
        )
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].unEditedMedia = [
            ...state.postData[p].unEditedMedia.filter(
              (obj) => obj.id !== action.payload.fileID
            )
          ];
        }
      }
    },
    reorderMediaFiles: (
      state,
      action: PayloadAction<{ platform: string | null; newOrder: any[] }>
    ) => {
      const { newOrder, platform } = action.payload;

      // Helper function to reorder media by id array and append unsorted items at the end
      const reorderAllMediaArray = (mediaArray, reorderedIds) => {
        // Map reordered IDs to corresponding media items

        const reorderedMedia = reorderedIds
          .map((uuid) =>
            mediaArray.find((item) =>
              item.source_media_uuid
                ? item?.source_media_uuid === uuid
                : item?.uuid === uuid
            )
          )
          .filter(Boolean); // Filter out any undefined values

        // Find any media items that were not in the reorderedIds and add them at the end
        const unsortedMedia = mediaArray.filter(
          (item) =>
            !reorderedIds.includes(item.uuid) &&
            !reorderedIds.includes(item.source_media_uuid)
        );

        return [...reorderedMedia, ...unsortedMedia];
      };
      const reorderMediaArray = (mediaArray, reorderedIds) => {
        // Map reordered IDs to corresponding media items

        const reorderedMedia = reorderedIds
          .map((uuid) => mediaArray.find((item) => item?.uuid === uuid))
          .filter(Boolean); // Filter out any undefined values

        // Find any media items that were not in the reorderedIds and add them at the end
        const unsortedMedia = mediaArray.filter(
          (item) => !reorderedIds.includes(item.uuid)
        );

        return [...reorderedMedia, ...unsortedMedia];
      };

      // Reorder for a specific platform

      state.postData[platform].media = reorderMediaArray(
        state.postData[platform].media,
        newOrder
      );

      // Reorder for all platforms if 'ALL' is specified
      if (platform === ALL) {
        for (const p of PLATFORM_LIST_WITH_ALL) {
          if (state.platformEnabled[p]) {
            state.postData[p].media = reorderAllMediaArray(
              state.postData[p].media,
              newOrder
            );
          }
        }
      }
    },

    addMediaUploadingFiles: (
      state,
      action: PayloadAction<{ platform: string | null; files: any[] }>
    ) => {
      // TODO: Clear uploading media files when the upload is finished
      // Where will I handle the upload? In the component or in the slice?

      state.postData[action.payload.platform].mediaUploading = [
        ...state.postData[action.payload.platform].mediaUploading,
        ...action.payload.files
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p].mediaUploading = [
              ...state.postData[p].mediaUploading,
              ...action.payload.files
            ];
          }
        }
      }
    },
    clearUnuploadedFiles: (
      state,
      action: PayloadAction<{ platform: string | null }>
    ) => {
      state.postData[action.payload.platform].mediaUploading = [];

      state.postData[action.payload.platform].media = state.postData[
        action.payload.platform
      ].media.filter((object) => object.uploaded !== false);

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p].mediaUploading = [];

            state.postData[p].media = state.postData[p].media.filter(
              (object) => object.uploaded !== false
            );
          }
        }
      }
    },
    deleteAfile: (
      state,
      action: PayloadAction<{ platform: string | null; fileID: any; uuid: any }>
    ) => {
      state.postData[action.payload.platform].media = [
        ...state.postData[action.payload.platform].media.filter(
          (obj) => obj.id !== action.payload.fileID
        )
      ];
      state.postData[action.payload.platform].media = [
        ...state.postData[action.payload.platform].media.filter(
          (obj) => obj?.source_media_uuid !== action.payload.fileID
        )
      ];
      state.postData[action.payload.platform].unEditedMedia = [
        ...state.postData[action.payload.platform].unEditedMedia?.filter(
          (obj) => obj?.id !== action.payload.fileID
        )
      ];

      state.postData[action.payload.platform].cropList = [
        ...state.postData[action.payload.platform].cropList.filter(
          (obj) => obj.uuid !== action.payload.uuid
        )
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].media = [
            ...state.postData[p].media.filter(
              (obj) => obj.id !== action.payload.fileID
            )
          ];

          state.postData[p].media = [
            ...state.postData[p].media.filter(
              (obj) => obj?.source_media_uuid !== action.payload.fileID
            )
          ];

          state.postData[p].unEditedMedia = [
            ...state.postData[p].unEditedMedia?.filter(
              (obj) => obj?.id !== action.payload.fileID
            )
          ];
          state.postData[p].cropList = [
            ...state.postData[p].cropList.filter(
              (obj) => obj.uuid !== action.payload.uuid
            )
          ];
        }
      }
    },
    deleteALoadingfile: (
      state,
      action: PayloadAction<{ platform: string | null; fileID: any }>
    ) => {
      state.postData[action.payload.platform].mediaUploading = [
        ...state.postData[action.payload.platform].mediaUploading.filter(
          (obj) => obj.id !== action.payload.fileID
        )
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].mediaUploading = [
            ...state.postData[p].mediaUploading.filter(
              (obj) => obj.id !== action.payload.fileID
            )
          ];
        }
      }
    },

    fileUploadStatus: (
      state,
      action: PayloadAction<{ platform: string | null; fileID: any }>
    ) => {
      state.postData[action.payload.platform].media = state.postData[
        action.payload.platform
      ].media.map((obj) => {
        if (obj.id === action.payload.fileID) {
          // Update the mediaType of the file with the specified fileID
          return {
            ...obj,
            uploaded: true // Set the new media_type here
          };
        }
        // Return unchanged object for other files
        return obj;
      });

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].media = state.postData[p].media.map((obj) => {
            if (obj.id === action.payload.fileID) {
              // Update the mediaType of the file with the specified fileID
              return {
                ...obj,
                uploaded: true // Set the new mediaType here
              };
            }
            // Return unchanged object for other files
            return obj;
          });
        }
      }
    },

    addDraggedFiles: (
      state,
      action: PayloadAction<{ platform: string | null; files: any[] }>
    ) => {
      // TODO: Clear uploading media files when the upload is finished
      // Where will I handle the upload? In the component or in the slice?

      state.postData[action.payload.platform].draggedMediaFiles = [
        ...action.payload.files
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p].draggedMediaFiles = [...action.payload.files];
          }
        }
      }
    },
    removeDraggedFiles: (
      state,
      action: PayloadAction<{ platform: string | null }>
    ) => {
      state.postData[action.payload.platform].draggedMediaFiles = [];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p].draggedMediaFiles = [];
          }
        }
      }
    },

    addMediaLibraryFiles: (
      state,
      action: PayloadAction<{ platform: string | null; files: any[] }>
    ) => {
      // TODO: Clear uploading media files when the upload is finished
      // Where will I handle the upload? In the component or in the slice?

      state.postData[action.payload.platform].mediaLibraryFiles = [
        ...action.payload.files
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p].mediaLibraryFiles = [...action.payload.files];
          }
        }
      }
    },
    removeMediaLibraryFiles: (
      state,
      action: PayloadAction<{ platform: string | null }>
    ) => {
      state.postData[action.payload.platform].mediaLibraryFiles = [];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p].mediaLibraryFiles = [];
          }
        }
      }
    },

    addPublicationDate: (
      state,
      action: PayloadAction<{ platform: string | null; date: any }>
    ) => {
      const { platform, date } = action.payload;

      state.postData[platform].tempPublicationData.date = date;

      if (platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].tempPublicationData.date = date;
        }

        state.all_platforms_publication_datetime_date = action.payload.date;
      }
    },

    addPublicationTime: (
      state,
      action: PayloadAction<{ platform: string | null; time: any }>
    ) => {
      const { platform, time } = action.payload;

      state.postData[platform].tempPublicationData.time = time;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].tempPublicationData.time = time;
        }

        state.all_platforms_publication_datetime_time = action.payload.time;
      }
    },

    clearPublicationDateTime: (
      state,
      action: PayloadAction<{ platform: string | null }>
    ) => {
      // TODO: Clear uploading media files when the upload is finished
      // Where will I handle the upload? In the component or in the slice?
      const { platform } = action.payload;
      state.postData[platform].tempPublicationData = {
        date: '',
        time: getDefaultBestTimeToPostValue(platform),
        datetime: null
      };

      // state.postData[platform
      // ].planned_publication_datetime_time = "";
      // state.postData[platform
      // ].planned_publication_datetime_date = null;
      // state.postData[platform].planned_publication_datetime =
      //   null;

      if (platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].tempPublicationData = {
            date: '',
            time: getDefaultBestTimeToPostValue(p),
            datetime: null
          };

          // state.postData[p].planned_publication_datetime_time = "";
          // state.postData[p].planned_publication_datetime_date = null;
          // state.postData[p].planned_publication_datetime = null;
        }

        state.all_platforms_publication_datetime_time = '';
        state.all_platforms_publication_datetime_date = null;
      }
    },

    addPublicationDateTime: (
      state,
      action: PayloadAction<{ platform: string | null; dateTime: any }>
    ) => {
      // TODO: Clear uploading media files when the upload is finished
      // Where will I handle the upload? In the component or in the slice?
      const { platform, dateTime } = action.payload;
      state.postData[platform].tempPublicationData.datetime = dateTime;

      // state.postData[platform].planned_publication_datetime =
      //   dateTime;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].tempPublicationData.datetime = dateTime;
          // state.postData[p].planned_publication_datetime =
          //  dateTime;
        }

        state.all_platforms_publication_datetime_time = action.payload.dateTime;
      }
    },

    addMainPublicationDateTime: (
      state,
      action: PayloadAction<{
        platform: string | null;
        dateTime: any;
      }>
    ) => {
      const { platform, dateTime } = action.payload;

      if (dateTime) {
        // use moment js get time on the same timezone of the dateTime
        const m = moment.parseZone(dateTime);
        const formattedTime = m.format('HH:mm');
        const formattedDate = m.format('YYYY-MM-DD');
        console.log(
          'formattedTime',
          formattedTime,
          'formattedDate',
          formattedDate
        );

        state.postData[platform].planned_publication_datetime = dateTime;
        state.postData[platform].planned_publication_datetime_time =
          formattedTime;
        state.postData[platform].planned_publication_datetime_date =
          formattedDate;

        state.postData[platform].tempPublicationData.date = formattedDate;
        state.postData[platform].tempPublicationData.time = formattedTime;
        state.postData[platform].tempPublicationData.datetime = dateTime;

        if (action.payload.platform === ALL) {
          // General media upload has been executed
          for (const p of PLATFORM_LIST) {
            state.postData[p].planned_publication_datetime = dateTime;
            state.postData[p].planned_publication_datetime_time = formattedTime;
            state.postData[p].planned_publication_datetime_date = formattedDate;

            state.postData[p].tempPublicationData.date = formattedDate;
            state.postData[p].tempPublicationData.time = formattedTime;
            state.postData[p].tempPublicationData.datetime = dateTime;

            state.postData[p].planned_publication_datetime = dateTime;
          }

          state.all_platforms_publication_datetime_time =
            action.payload.dateTime;
        }
      }
    },

    addTime: (state, action: PayloadAction<{ Time: any }>) => {
      state.schedulePlanningData.time = action.payload.Time;
    },
    addPublicationDelay: (state, action: PayloadAction<{ Time: any }>) => {
      state.schedulePlanningData.publication_delay_in_minutes =
        action.payload.Time;
    },
    addRecurringStr: (state, action: PayloadAction<{ recurrence: string }>) => {
      state.schedulePlanningData.recurrence_as_str = action.payload.recurrence;
    },

    updatePublishingDelayData: (
      state,
      action: PayloadAction<{
        attribute: string;
        value: any;
      }>
    ) => {
      state.temporaryPublishingDelayData[action.payload.attribute] =
        action.payload.value;
    },

    clearRecurrenceData: (state, action: PayloadAction<{}>) => {
      state.schedulePlanningData.time = getDefaultBestTimeToPostValue(ALL);
      state.schedulePlanningData.recurrence_as_str = '';
      state.schedulePlanningData.is_recurring_post = false;
      state.schedulePlanningData.publication_delay_in_minutes = '';
    },
    clearPublishingDelayData: (state, action: PayloadAction<{}>) => {
      state.temporaryPublishingDelayData.noPublishEndTime = '';
      state.temporaryPublishingDelayData.noPublishStartTime = '';
      state.temporaryPublishingDelayData.noPublishDays = [];
      state.temporaryPublishingDelayData.days = null;
      state.temporaryPublishingDelayData.hours = null;
      state.temporaryPublishingDelayData.minutes = null;
      state.temporaryPublishingDelayData.totalMinutes = null;
    },

    updateRecurringStatus: (state, action: PayloadAction<{}>) => {
      const { time, recurrence_as_str, is_recurring_post } =
        state.schedulePlanningData;

      if (time && recurrence_as_str) {
        state.schedulePlanningData.is_recurring_post = true;
      } else {
        state.schedulePlanningData.is_recurring_post = false;
      }
    },
    toggleActivePlatform: (
      state,
      action: PayloadAction<{ platform: string; locationUUid: string }>
    ) => {
      const key = `${action.payload.locationUUid}_${action.payload.platform}`;
      if (state.creator_multipost_config[key]) {
        delete state.creator_multipost_config[key];
      } else state.creator_multipost_config[key] = true;
    },
    toggleActivePlatformTemp: (
      state,
      action: PayloadAction<{ platform: string; locationUUid: string }>
    ) => {
      const key = `${action.payload.locationUUid}_${action.payload.platform}`;
      if (state.tempState.creator_multipost_config[key]) {
        delete state.tempState.creator_multipost_config[key];
      } else state.tempState.creator_multipost_config[key] = true;
    },
    removeActivePlatform: (
      state,
      action: PayloadAction<{ platform: string; locationUUid: string }>
    ) => {
      const key = `${action.payload.locationUUid}_${action.payload.platform}`;
      if (state.creator_multipost_config[key]) {
        delete state.creator_multipost_config[key];
      }
    },
    updatePostKey: (
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) => {
      state[action.payload.key] = action.payload.value;
    },
    updatePostDataKey: (
      state,
      action: PayloadAction<{
        platform: string | null;
        key: string;
        value: any;
      }>
    ) => {
      state.postData[action.payload.platform][action.payload.key] =
        action.payload.value;
    },
    removeActivePlatformTemp: (
      state,
      action: PayloadAction<{ platform: string; locationUUid: string }>
    ) => {
      const key = `${action.payload.locationUUid}_${action.payload.platform}`;
      if (state.tempState.creator_multipost_config[key]) {
        delete state.tempState.creator_multipost_config[key];
      }
    },
    addActivePlatform: (
      state,
      action: PayloadAction<{ platform: string; locationUUid: string }>
    ) => {
      console.log('adding active platform,');
      const key = `${action.payload.locationUUid}_${action.payload.platform}`;
      state.creator_multipost_config[key] = true;
    },
    addActivePlatformTemp: (
      state,
      action: PayloadAction<{ platform: string; locationUUid: string }>
    ) => {
      const key = `${action.payload.locationUUid}_${action.payload.platform}`;
      state.tempState.creator_multipost_config[key] = true;
    },
    resetActiveLocations: (state, action: PayloadAction<{}>) => {
      state.creator_multipost_config = {};
    },
    resetActiveLocationsTemp: (state, action: PayloadAction<{}>) => {
      state.tempState.creator_multipost_config = {};
    },
    updatePostText: (
      state,
      action: PayloadAction<{
        platform: string | null;
        text: string;
        text_as_html: string;
        textText?: string;
        urlPreview: any;
        url_for_linkpreview?: any;
        fullCharacterCount?: number | null;
      }>
    ) => {
      state.postData[action.payload.platform].text_as_text =
        action.payload?.textText;
      state.postData[action.payload.platform].text = action.payload.text;

      state.postData[action.payload.platform].text_as_html =
        action.payload.text_as_html;
      if (action.payload.platform === ALL) {
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            if (p === TWITTER) {
              const tweet = twitterText.parseTweet(
                action.payload.textText?.trim()
              );

              state.postData[p].text_length = tweet.weightedLength;
              state.postData[p].text_length_striped = tweet.weightedLength;
            } else {
              state.postData[p].text_length = action.payload.fullCharacterCount;
              state.postData[p].text_length_striped =
                action.payload.textText?.trim().length;
            }
          }
        }
      } else if (action.payload.platform === TWITTER) {
        const tweet = twitterText.parseTweet(action.payload.textText?.trim());

        state.postData[action.payload.platform].text_length =
          tweet.weightedLength;
        state.postData[action.payload.platform].text_length_striped =
          tweet.weightedLength;
      } else {
        state.postData[action.payload.platform].text_length =
          action.payload.fullCharacterCount;
        state.postData[action.payload.platform].text_length_striped =
          action.payload.textText?.trim().length;
      }

      state.postData[action.payload.platform].urlPreview =
        action.payload.urlPreview;
      state.postData[action.payload.platform].url_for_linkpreview =
        action.payload.url_for_linkpreview;
    },
    addCreatorText: (
      state,
      action: PayloadAction<{
        platform: string | null;
        text: string;
      }>
    ) => {
      state.postData[action.payload.platform].text = action.payload.text;
    },
    updateUrlPreviewData: (
      state,
      action: PayloadAction<{
        platform: string | null;
        data: any;
        url_for_linkpreview: string | null;
      }>
    ) => {
      state.postData[action.payload.platform].urlPreview = action.payload.data;
      state.postData[action.payload.platform].url_for_linkpreview =
        action.payload.url_for_linkpreview;
    },

    addImageThumbnail: (
      state,
      action: PayloadAction<{
        url: any;
        file: any;
        platform: string | null;
      }>
    ) => {
      state.uploadedImageThumbnail = action.payload.url;

      state.postData[action.payload.platform].uploadedImageThumbnail.url =
        action.payload.url;
      state.postData[action.payload.platform].video_thumbnail_background_image =
        action.payload.url;
      state.postData[action.payload.platform].uploadedImageThumbnail.file =
        action.payload.file;

      state.postData[action.payload.platform].editedImageThumbnail.url =
        action.payload.url;
      state.postData[action.payload.platform].editedImageThumbnail.file =
        action.payload.file;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].uploadedImageThumbnail.url = action.payload.url;
          state.postData[p].video_thumbnail_background_image =
            action.payload.url;
          state.postData[p].editedImageThumbnail.url = action.payload.url;
          state.postData[p].uploadedImageThumbnail.file = action.payload.file;
          state.postData[p].editedImageThumbnail.file = action.payload.file;
        }
      }
    },

    editImageThumbnail: (
      state,
      action: PayloadAction<{ url: any; file: any; platform: string | null }>
    ) => {
      state.postData[action.payload.platform].editedImageThumbnail.url =
        action.payload.url;
      state.postData[action.payload.platform].editedImageThumbnail.file =
        action.payload.file;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].editedImageThumbnail.url = action.payload.url;
          state.postData[p].editedImageThumbnail.file = action.payload.file;
        }
      }
    },
    clearImageThumbnail: (
      state,
      action: PayloadAction<{ platform: string | null }>
    ) => {
      state.uploadedImageThumbnail = '';

      const postDataObj = state.postData[action.payload.platform];

      postDataObj.uploadedImageThumbnail.url = '';
      postDataObj.uploadedImageThumbnail.file = null;
      postDataObj.uploadedImageThumbnail.uploadPresets = null;
      postDataObj.uploadedImageThumbnail.media = {};

      postDataObj.editedImageThumbnail.url = '';
      postDataObj.editedImageThumbnail.file = null;
      postDataObj.editedImageThumbnail.uploadPresets = null;

      postDataObj.layeredImageThumbnail.file = null;
      postDataObj.layeredImageThumbnail.uploaded = false;
      postDataObj.layeredImageThumbnail.uploadPresets = null;
      postDataObj.layeredImageThumbnail.media = {};
      postDataObj.fullEditorImage.uploaded = false;
      postDataObj.fullEditorImage.url = '';
      postDataObj.fullEditorImage.file = null;

      postDataObj.video_thumbnail = {};
      postDataObj.video_thumbnail_editor = {};
      postDataObj.video_thumbnail_background_image = '';

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].uploadedImageThumbnail.url = '';
          state.postData[p].uploadedImageThumbnail.file = null;
          state.postData[p].uploadedImageThumbnail.uploadPresets = null;
          state.postData[p].uploadedImageThumbnail.media = {};
          state.postData[p].editedImageThumbnail.url = '';
          state.postData[p].editedImageThumbnail.file = null;
          state.postData[p].editedImageThumbnail.uploadPresets = null;

          state.postData[p].layeredImageThumbnail.file = null;
          state.postData[p].layeredImageThumbnail.uploaded = false;
          state.postData[p].layeredImageThumbnail.uploadPresets = null;
          state.postData[p].layeredImageThumbnail.media = {};
          state.postData[p].fullEditorImage.uploaded = false;
          state.postData[p].fullEditorImage.url = '';
          state.postData[p].fullEditorImage.file = null;

          state.postData[p].video_thumbnail = {};
          state.postData[p].video_thumbnail_editor = {};
          state.postData[p].video_thumbnail_background_image = '';
        }
      }
    },
    cancelImageThumbnail: (
      state,
      action: PayloadAction<{ platform: string | null }>
    ) => {
      state.uploadedImageThumbnail = '';

      const postDataObj = state.postData[action.payload.platform];

      postDataObj.uploadedImageThumbnail.url = '';
      postDataObj.uploadedImageThumbnail.file = null;
      postDataObj.uploadedImageThumbnail.uploadPresets = null;
      postDataObj.uploadedImageThumbnail.media = {};

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].uploadedImageThumbnail.url = '';
          state.postData[p].uploadedImageThumbnail.file = null;
          state.postData[p].uploadedImageThumbnail.uploadPresets = null;
          state.postData[p].uploadedImageThumbnail.media = {};
        }
      }
    },

    layerImageThumbnail: (
      state,
      action: PayloadAction<{ file: any; platform: string | null }>
    ) => {
      state.postData[action.payload.platform].layeredImageThumbnail.file =
        action.payload.file;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].layeredImageThumbnail.file = action.payload.file;
        }
      }
    },

    updateUploadedImagePresets: (
      state,
      action: PayloadAction<{ presets: any; platform: string | null }>
    ) => {
      state.postData[
        action.payload.platform
      ].uploadedImageThumbnail.uploadPresets = action.payload.presets;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].uploadedImageThumbnail.uploadPresets =
            action.payload.presets;
        }
      }
    },
    updateEditorImagePresets: (
      state,
      action: PayloadAction<{ presets: any; platform: string | null }>
    ) => {
      state.postData[action.payload.platform].fullEditorImage.uploadPresets =
        action.payload.presets;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].fullEditorImage.uploadPresets =
            action.payload.presets;
        }
      }
    },
    updateEditedImagePresets: (
      state,
      action: PayloadAction<{ presets: any; platform: string | null }>
    ) => {
      state.postData[
        action.payload.platform
      ].editedImageThumbnail.uploadPresets = action.payload.presets;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].editedImageThumbnail.uploadPresets =
            action.payload.presets;
        }
      }
    },

    updateLayeredImagePresets: (
      state,
      action: PayloadAction<{
        presets: any;
        platform: string | null;
      }>
    ) => {
      state.postData[
        action.payload.platform
      ].layeredImageThumbnail.uploadPresets = action.payload.presets;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].layeredImageThumbnail.uploadPresets =
            action.payload.presets;
        }
      }
    },
    updateLayeredImageState: (
      state,
      action: PayloadAction<{
        uploadState: boolean;
        platform: string | null;
        media: any;
      }>
    ) => {
      const { media, platform, uploadState } = action.payload;
      state.postData[action.payload.platform].layeredImageThumbnail.uploaded =
        action.payload.uploadState;
      state.postData[action.payload.platform].layeredImageThumbnail.media =
        action.payload.media;

      state.postData[action.payload.platform].video_thumbnail = media;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].layeredImageThumbnail.uploaded =
            action.payload.uploadState;
          state.postData[p].layeredImageThumbnail.media = action.payload.media;
          state.postData[p].video_thumbnail = media;
        }
      }
    },

    updateEditedImageState: (
      state,
      action: PayloadAction<{
        uploadState: boolean;
        platform: string | null;
        media: any;
      }>
    ) => {
      const { media, platform, uploadState } = action.payload;
      state.postData[action.payload.platform].editedImageThumbnail.uploaded =
        action.payload.uploadState;
      state.postData[action.payload.platform].editedImageThumbnail.media =
        action.payload.media;

      state.postData[action.payload.platform].video_thumbnail_editor = media;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].editedImageThumbnail.uploaded =
            action.payload.uploadState;
          state.postData[p].editedImageThumbnail.media = action.payload.media;
          state.postData[p].video_thumbnail_editor = media;
        }
      }
    },

    saveEditorFullImage: (
      state,
      action: PayloadAction<{
        url: any;
        file: any;
        currentCrop: any;
        platform: string | null;
        uploadState: boolean;
      }>
    ) => {
      state.postData[action.payload.platform].fullEditorImage.url =
        action.payload.url;
      state.postData[action.payload.platform].fullEditorImage.file =
        action.payload.file;
      state.postData[action.payload.platform].fullEditorImage.currentCrop =
        action.payload.currentCrop;
      state.postData[action.payload.platform].fullEditorImage.uploaded =
        action.payload.uploadState;
      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].fullEditorImage.url = action.payload.url;
          state.postData[p].fullEditorImage.file = action.payload.file;
          state.postData[p].fullEditorImage.currentCrop =
            action.payload.currentCrop;
          state.postData[p].fullEditorImage.uploaded =
            action.payload.uploadState;
        }
      }
    },

    updateThumbnailProcessing: (
      state,
      action: PayloadAction<{
        state: boolean;
        platform: string | null;
      }>
    ) => {
      state.postData[action.payload.platform].thumbnail_preview_processing =
        action.payload.state;

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          state.postData[p].thumbnail_preview_processing = action.payload.state;
        }
      }
    },

    updateMediaAssetFile: (
      state,
      action: PayloadAction<{
        file: any;
        preview_image: string;
        assetBlob: any;
        uuid: string | null;
        platform: string | null;
        uploadPresets: any;
        source_media_uuid: any;
        width: string | number;
        height: string | number;
      }>
    ) => {
      const {
        platform,
        uuid,
        file,
        preview_image,
        assetBlob,
        uploadPresets,
        source_media_uuid,
        width,
        height
      } = action.payload;
      const mediaItem = state.postData[platform].media.find(
        (obj) => obj.uuid === uuid
      );

      mediaItem.file = file;
      mediaItem.preview_image =
        uploadPresets?.uploaded_object_data.preview_image_url;
      mediaItem.assetBlob = assetBlob;
      mediaItem.uploadPreset = uploadPresets;
      mediaItem.source_media_uuid = source_media_uuid;

      mediaItem.uuid = uploadPresets?.uuid;
      mediaItem.id = uploadPresets?.uuid;
      mediaItem.previewImageURL =
        uploadPresets?.uploaded_object_data.preview_image_url;

      mediaItem.thumbnail_image = uploadPresets?.thumbnail_url;
      mediaItem.original_asset =
        uploadPresets?.uploaded_object_data.original_asset;
      mediaItem.width = width;
      mediaItem.height = height;

      state.postData[platform].cropList = [
        ...state.postData[platform].cropList.filter((obj) => obj.uuid !== uuid)
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            const platformMediaItem = state.postData[p]?.media?.find(
              (obj) => obj?.uuid === uuid
            );
            platformMediaItem.file = file;
            platformMediaItem.preview_image =
              uploadPresets?.uploaded_object_data.preview_image_url;
            platformMediaItem.assetBlob = assetBlob;
            platformMediaItem.uploadPreset = uploadPresets;
            platformMediaItem.source_media_uuid = source_media_uuid;

            platformMediaItem.uuid = uploadPresets?.uuid;
            platformMediaItem.id = uploadPresets?.uuid;
            platformMediaItem.previewImageURL =
              uploadPresets?.uploaded_object_data.preview_image_url;
            platformMediaItem.thumbnail_image = uploadPresets?.thumbnail_url;
            platformMediaItem.original_asset =
              uploadPresets?.uploaded_object_data.original_asset;
            platformMediaItem.width = width;
            platformMediaItem.height = height;

            state.postData[p].cropList = [
              ...state.postData[p].cropList.filter((obj) => obj.uuid !== uuid)
            ];
          }
        }
      }
    },
    replaceMediaAssetFile: (
      state,
      action: PayloadAction<{
        file: any;
        preview_image: string;
        assetBlob: any;
        original_uuid: string | null;
        uuid: string | null;
        platform: string | null;
        uploadPresets: any;
        source_media_uuid: any;
        width: string | number;
        height: string | number;
      }>
    ) => {
      const {
        platform,
        uuid,
        original_uuid,
        file,
        preview_image,
        assetBlob,
        uploadPresets,
        source_media_uuid,
        width,
        height
      } = action.payload;
      const mediaItem = state.postData[platform].media.find(
        (obj) => obj.uuid === original_uuid
      );
      if (!mediaItem) {
        console.error(
          'Media item not found for uuid:',
          original_uuid,
          'on platform:',
          platform
        );
        return;
      }

      mediaItem.preview_image =
        uploadPresets?.uploaded_object_data.preview_image_url;
      mediaItem.assetBlob = assetBlob;
      mediaItem.uploadPreset = uploadPresets;
      mediaItem.source_media_uuid = source_media_uuid;

      mediaItem.uuid = uploadPresets?.uuid;
      mediaItem.id = uploadPresets?.uuid;
      mediaItem.previewImageURL =
        uploadPresets?.uploaded_object_data.preview_image_url;

      mediaItem.thumbnail_image = uploadPresets?.thumbnail_url;
      mediaItem.original_asset =
        uploadPresets?.uploaded_object_data.original_asset;
      mediaItem.width = width;
      mediaItem.height = height;

      state.postData[platform].cropList = [
        ...state.postData[platform].cropList.filter((obj) => obj.uuid !== uuid)
      ];

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            const platformMediaItem = state.postData[p]?.media?.find(
              (obj) => obj.uuid === original_uuid
            );

            platformMediaItem.preview_image =
              uploadPresets?.uploaded_object_data.preview_image_url;
            platformMediaItem.assetBlob = assetBlob;
            platformMediaItem.uploadPreset = uploadPresets;
            platformMediaItem.source_media_uuid = source_media_uuid;

            platformMediaItem.uuid = uploadPresets?.uuid;
            platformMediaItem.id = uploadPresets?.uuid;
            platformMediaItem.previewImageURL =
              uploadPresets?.uploaded_object_data.preview_image_url;
            platformMediaItem.thumbnail_image = uploadPresets?.thumbnail_url;
            platformMediaItem.original_asset =
              uploadPresets?.uploaded_object_data.original_asset;
            platformMediaItem.width = width;
            platformMediaItem.height = height;

            state.postData[p].cropList = [
              ...state.postData[p].cropList.filter((obj) => obj.uuid !== uuid)
            ];
          }
        }
      }
    },

    updateEditorUpdateState: (
      state,
      action: PayloadAction<{
        platform: string | null;
        state: boolean;
        uuid: string | null;
      }>
    ) => {
      state.postData[action.payload.platform].media.find(
        (obj) => obj.uuid === action.payload.uuid
      ).editorUpdateState = action.payload.state;
    },

    updateEditorCloseState: (
      state,
      action: PayloadAction<{
        state: boolean;
      }>
    ) => {
      state.allowEditorClose = action.payload.state;
    },
    updateBrandEditorCloseState: (
      state,
      action: PayloadAction<{
        state: boolean;
      }>
    ) => {
      state.allowBrandEditorClose = action.payload.state;
    },
    updateOverlayCreatorCloseState: (
      state,
      action: PayloadAction<{
        state: boolean;
      }>
    ) => {
      state.allowOverlayCreatorClose = action.payload.state;
    },
    updateThumbnailCloseState: (
      state,
      action: PayloadAction<{
        state: boolean;
      }>
    ) => {
      state.allowThumbnailEditorClose = action.payload.state;
    },

    updateMultiImageEditorCloseState: (
      state,
      action: PayloadAction<{
        state: boolean;
      }>
    ) => {
      state.allowMultiImageEditor = action.payload.state;
    },
    updateIGTagModalCloseState: (
      state,
      action: PayloadAction<{
        state: boolean;
      }>
    ) => {
      state.postData[INSTAGRAM].allowTagModalClose = action.payload.state;
    },
    updateIGProductTagModalCloseState: (
      state,
      action: PayloadAction<{
        state: boolean;
      }>
    ) => {
      state.postData[INSTAGRAM].allowProductTagModalClose =
        action.payload.state;
    },
    updatePlatformSelectionFilter: (
      state,
      action: PayloadAction<{
        platform: string;
      }>
    ) => {
      if (state.selectionFilters.byPlatform.includes(action.payload.platform)) {
        state.selectionFilters.byPlatform =
          state.selectionFilters.byPlatform.filter(
            (platform) => platform !== action.payload.platform
          );
      } else state.selectionFilters.byPlatform.push(action.payload.platform);
    },
    updateTagSelectionFilter: (
      state,
      action: PayloadAction<{
        tag: string;
      }>
    ) => {
      if (state.selectionFilters.byTag.includes(action.payload.tag)) {
        state.selectionFilters.byTag = state.selectionFilters.byTag.filter(
          (tag) => tag !== action.payload.tag
        );
      } else state.selectionFilters.byTag.push(action.payload.tag);
    },
    setShowOnlySelectedPlatforms: (
      state,
      action: PayloadAction<{ showOnlySelectedPlatforms: boolean }>
    ) => {
      state.ui.showOnlySelectedPlatforms =
        action.payload.showOnlySelectedPlatforms;
    },
    setUploadAreaVisibility: (
      state,
      action: PayloadAction<{ visible: boolean }>
    ) => {
      state.ui.uploadAreaVisible = action.payload.visible;
    },
    setShowProfileGroupSelectModal: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.tempState.creator_multipost_config =
          state.creator_multipost_config;
      }
      state.ui.showProfileGroupSelectModal = action.payload;
    },
    syncProfileGroupSelectState: (state) => {
      state.creator_multipost_config = {
        ...state.tempState.creator_multipost_config
      };
    },
    setScheduleModalToggle: (state, action: PayloadAction<boolean>) => {
      state.ui.scheduleModalToggle = action.payload;
    },
    setShowEditImageModal: (state, action: PayloadAction<boolean>) => {
      state.ui.showEditImageModal = action.payload;
    },
    setGeneralPostType: (state, action: PayloadAction<string | null>) => {
      state.general_post_type = action.payload;
    },
    setPlatformFirstComment: (
      state,
      action: PayloadAction<{ platform: string; firstComment: string }>
    ) => {
      state.postData[action.payload.platform].first_comment =
        action.payload.firstComment;
    },

    setSliceAttribute: (
      state,
      action: PayloadAction<{
        attribute: string;
        value: any;
      }>
    ) => {
      state[action.payload.attribute] = action.payload.value;
    },
    setPostDataAttribute: (
      state,
      action: PayloadAction<{
        platform: string;
        attribute: string;
        value: any;
      }>
    ) => {
      state.postData[action.payload.platform][action.payload.attribute] =
        action.payload.value;

      if (action.payload.platform === ALL) {
        // General media upload has been executed

        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p][action.payload.attribute] = action.payload.value;
          }
        }
      }
    },

    updateIGTags: (
      state,
      action: PayloadAction<{
        uuid: any;
        newTags: any[];
      }>
    ) => {
      const { uuid, newTags } = action.payload;
      const index = state.postData[INSTAGRAM].instagram_temp_tags.findIndex(
        (obj) => obj.uuid === uuid
      );

      if (index !== -1) {
        // If uuid exists, update the tags of the existing object
        state.postData[INSTAGRAM].instagram_temp_tags[index].tags = newTags;
      } else {
        // If uuid doesn't exist, create a new object with the uuid and tags
        state.postData[INSTAGRAM].instagram_temp_tags = [
          ...state.postData[INSTAGRAM].instagram_temp_tags,
          { uuid: uuid, tags: newTags }
        ];
      }
    },
    updateProductIGTags: (
      state,
      action: PayloadAction<{
        uuid: string;
        newTags: any[];
      }>
    ) => {
      const { uuid, newTags } = action.payload;
      const instagramData = state.postData[INSTAGRAM];

      // Ensure the nested structure exists
      if (
        !instagramData.additional_publication_data
          .instagram_shopping_product_tags
      ) {
        instagramData.additional_publication_data.instagram_shopping_product_tags =
          {};
      }

      // Set or update the tags for the image_uuid key directly
      state.postData[
        INSTAGRAM
      ].additional_publication_data.instagram_shopping_product_tags[uuid] =
        newTags;
    },
    updateProductCatalog: (
      state,
      action: PayloadAction<{
        catalog: Catalog;
      }>
    ) => {
      const { catalog } = action.payload;
      state.postData[
        INSTAGRAM
      ].additional_publication_data.instagram_shopping_select_store = catalog;
    },

    deleteATag: (
      state,
      action: PayloadAction<{
        uuid: any;
        tagIndex: number;
      }>
    ) => {
      const { uuid, tagIndex } = action.payload;
      const objectIndex = state.postData[
        INSTAGRAM
      ].instagram_temp_tags.findIndex((obj) => obj.uuid === uuid);

      if (objectIndex !== -1) {
        // If uuid exists, remove the tag at the specified index
        state.postData[INSTAGRAM].instagram_temp_tags[objectIndex].tags =
          state.postData[INSTAGRAM].instagram_temp_tags[
            objectIndex
          ].tags.filter((_, index) => index !== tagIndex);
      }
    },
    deleteAProductTag: (
      state,
      action: PayloadAction<{
        uuid: string;
        tagIndex: number;
      }>
    ) => {
      const { uuid, tagIndex } = action.payload;

      const tagsForImage =
        state.postData[INSTAGRAM].additional_publication_data
          .instagram_shopping_product_tags[uuid];

      if (Array.isArray(tagsForImage)) {
        // Filter out the tag at the specified index
        state.postData[
          INSTAGRAM
        ].additional_publication_data.instagram_shopping_product_tags[uuid] =
          tagsForImage.filter((_, index) => index !== tagIndex);

        // Optional: Remove the key if no tags remain
        if (
          state.postData[INSTAGRAM].additional_publication_data
            .instagram_shopping_product_tags[uuid].length === 0
        ) {
          delete state.postData[INSTAGRAM].additional_publication_data
            .instagram_shopping_product_tags[uuid];
        }
      }
    },

    addIGCollaborator: (
      state,
      action: PayloadAction<{
        newCollaborator: any;
      }>
    ) => {
      state.postData[INSTAGRAM].instagram_collaborators = [
        ...state.postData[INSTAGRAM].instagram_collaborators,
        action.payload.newCollaborator
      ];
    },
    deleteACollaborator: (
      state,
      action: PayloadAction<{
        collabIndex: number;
      }>
    ) => {
      const { collabIndex } = action.payload;
      if (
        collabIndex >= 0 &&
        collabIndex < state.postData[INSTAGRAM].instagram_collaborators.length
      ) {
        state.postData[INSTAGRAM].instagram_collaborators = state.postData[
          INSTAGRAM
        ].instagram_collaborators.filter((_, index) => index !== collabIndex);
      }
    },
    loadPostToState: (state, action: PayloadAction<any>) => {
      // loop through postData keys and update the state
      const postData = action.payload.postData;

      // it can happen that these lists are saved in the backend as null values. To address this,
      // we need to check if they are null and set them to empty arrays if they are
      for (const key in postData) {
        for (let field of ['instagram_collaborators', 'instagram_user_tags']) {
          if (!postData[field]) continue;

          if (!postData[field]?.instagram_collaborators) {
            postData[field].instagram_collaborators = [];
          }
        }

        state.postData[key] = { ...state.postData[key], ...postData[key] };
      }
      // loop through keys and update the state if key exists
      for (const key in action.payload) {
        if (key === 'postData') continue;
        if (key in state) {
          state[key] = action.payload[key];
        }
      }

      // Load post boost configuration if present
      if (action.payload.postBoost) {
        state.postBoost = { ...state.postBoost, ...action.payload.postBoost };

        // Check if any boost configuration already exists and set the flag
        const hasExistingConfig =
          action.payload.postBoost.boost_config &&
          Object.keys(action.payload.postBoost.boost_config).some(
            (platform) => {
              const config = action.payload.postBoost.boost_config[platform];
              return (
                config &&
                Object.keys(config).length > 0 &&
                (config.active ||
                  config.goal ||
                  config.beneficiary ||
                  config.budget ||
                  config.days ||
                  config.special_requirements ||
                  (config.interests && config.interests.length > 0) ||
                  (config.countries && config.countries.length > 0) ||
                  (config.cities && config.cities.length > 0) ||
                  (config.gender && config.gender.length > 0) ||
                  config.min_age ||
                  config.max_age)
              );
            }
          );

        state.postBoost.configAlreadySaved = hasExistingConfig || false;

        console.log('🔄 loadPostToState - Post Boost Config:', {
          hasPostBoostInPayload: !!action.payload.postBoost,
          boostConfig: action.payload.postBoost.boost_config,
          hasExistingConfig,
          configAlreadySaved: state.postBoost.configAlreadySaved
        });
      } else {
        // No boost config in payload, so no existing config
        state.postBoost.configAlreadySaved = false;
        console.log(
          '🔄 loadPostToState - No Post Boost Config in payload, setting configAlreadySaved to false'
        );
      }
    },
    saveCroppedImageCache: (
      state,
      action: PayloadAction<{ key: string; obj: any }>
    ) => {
      state.cropsCache[action.payload.key] = action.payload.obj;
    },

    setMediaDataAttribute: (
      state,
      action: PayloadAction<{
        platform: string;
        uuid: string;
        attribute: string;
        value: boolean;
      }>
    ) => {
      state.postData[action.payload.platform].media.find(
        (obj) => obj.uuid === action.payload.uuid
      )[action.payload.attribute] = action.payload.value;

      if (action.payload.platform === ALL) {
        // General media upload has been executed

        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            state.postData[p].media.find(
              (obj) => obj.uuid === action.payload.uuid
            )[action.payload.attribute] = action.payload.value;
          }
        }
      }
    },
    updateCancelToken: (
      state,
      action: PayloadAction<{
        token: any;
      }>
    ) => {
      state.uploadCancelToken = action.payload.token;
    },

    addCropObject: (
      state,
      action: PayloadAction<{ platform: string | null; cropInfo: any }>
    ) => {
      const { cropInfo, platform } = action.payload;

      // It can happen that we have -0 for 0. Fixing it, because we cannot have negative pixels
      cropInfo.x = Math.abs(cropInfo.x);
      cropInfo.y = Math.abs(cropInfo.y);

      const aspectRatioWidth = cropInfo.aspect_ratio_width;
      const aspectRatioHeight = cropInfo.aspect_ratio_height;

      function closestIntegerHeightWidth(startWidth) {
        let widthAbove = startWidth;
        let widthBelow = startWidth;

        // Find closest width above that results in an integer height
        while (true) {
          const heightAbove =
            (widthAbove * aspectRatioHeight) / aspectRatioWidth;
          if (Number.isInteger(heightAbove)) {
            break;
          }
          widthAbove++;
        }

        // Find closest width below that results in an integer height
        while (widthBelow > 0) {
          const heightBelow =
            (widthBelow * aspectRatioHeight) / aspectRatioWidth;
          if (Number.isInteger(heightBelow)) {
            break;
          }
          widthBelow--;
        }

        return {
          closestAbove: {
            width: widthAbove,
            height: (widthAbove * aspectRatioHeight) / aspectRatioWidth
          },
          closestBelow: {
            width: widthBelow,
            height: (widthBelow * aspectRatioHeight) / aspectRatioWidth
          }
        };
      }

      if (aspectRatioHeight > 0 && aspectRatioWidth > 0) {
        // It can happen that the returned crop is not 100% the aspect ratio. We are correcting it here
        // even though it means adjust the crop width and height by a few pixels. This is important, because
        // platforms such as Instagram are very picky about the aspect ratio of the images
        const lowerWidth = Math.floor(cropInfo.width);

        const result = closestIntegerHeightWidth(lowerWidth);

        cropInfo.width = result.closestBelow.width;
        cropInfo.height = result.closestBelow.height;
      }

      const cropItem = state.postData[platform].cropList?.find(
        (obj) => obj.uuid === cropInfo.uuid
      );

      if (cropItem) {
        cropItem.width = cropInfo.width;
        cropItem.height = cropInfo.height;
        cropItem.x = cropInfo.x;
        cropItem.y = cropInfo.y;
        cropItem.aspect_ratio_width = cropInfo.aspect_ratio_width;
        cropItem.aspect_ratio_height = cropInfo.aspect_ratio_height;
      } else {
        state.postData[action.payload.platform].cropList = [
          ...state.postData[action.payload.platform].cropList,
          action.payload.cropInfo
        ];
      }

      if (action.payload.platform === ALL) {
        // General media upload has been executed
        for (const p of PLATFORM_LIST) {
          if (state.platformEnabled[p]) {
            const cropItem = state.postData[p].cropInfo?.find(
              (obj) => obj.uuid === cropInfo.uuid
            );

            if (cropItem) {
              cropItem.width = cropInfo.width;
              cropItem.height = cropInfo.height;
              cropItem.x = cropInfo.x;
              cropItem.y = cropInfo.y;
              cropItem.aspect_ratio_width = cropInfo.aspect_ratio_width;
              cropItem.aspect_ratio_height = cropInfo.aspect_ratio_height;
            } else {
              state.postData[p].cropList = [
                ...state.postData[p].cropList,
                action.payload.cropInfo
              ];
            }
          }
        }
      }

      state.lastCropUpdateTimestamp = Date.now().toString();
    },

    updateAssetUploadingState: (
      state,
      action: PayloadAction<{
        state: boolean;
      }>
    ) => {
      state.assetUploading = action.payload.state;
    },
    switchPreviewMode: (state, action: PayloadAction<{ mode: string }>) => {
      state.ui.previewMode = action.payload.mode;
    },
    setModalUiContent: (
      state,
      action: PayloadAction<{
        modal: string;
        attribute: string;
        value: any;
      }>
    ) => {
      const { modal, attribute, value } = action.payload;

      state.modalStates[modal][attribute] = value;
    },
    tempToMainScheduleData: (state) => {
      state.time = state.schedulePlanningData.time;
      state.recurrence_as_str = state.schedulePlanningData.recurrence_as_str;
      state.is_recurring_post = state.schedulePlanningData.is_recurring_post;
      state.publication_delay_in_minutes =
        state.schedulePlanningData.publication_delay_in_minutes;

      for (const key in state.publishingDelayData) {
        if (state.temporaryPublishingDelayData.hasOwnProperty(key)) {
          state.publishingDelayData[key] =
            state.temporaryPublishingDelayData[key];
        }
      }

      for (const p of PLATFORM_LIST_WITH_ALL) {
        state.postData[p].planned_publication_datetime_time =
          state.postData[p].tempPublicationData.time;
        state.postData[p].planned_publication_datetime_date =
          state.postData[p].tempPublicationData.date;
        state.postData[p].planned_publication_datetime =
          state.postData[p].tempPublicationData.datetime;
      }
    },

    mainToTempScheduleData: (state) => {
      console.log(
        'mainToTempScheduleData',
        extractTimeFromRecurrence(state.recurrence_as_str, state.timeZone)
      );
      state.schedulePlanningData.time = state.recurrence_as_str
        ? extractTimeFromRecurrence(state.recurrence_as_str, state.timeZone)
        : getDefaultBestTimeToPostValue(ALL);
      state.schedulePlanningData.recurrence_as_str = state.recurrence_as_str;
      state.schedulePlanningData.is_recurring_post = state.is_recurring_post;

      state.schedulePlanningData.publication_delay_in_minutes =
        state.publication_delay_in_minutes;

      for (const key in state.temporaryPublishingDelayData) {
        if (state.publishingDelayData.hasOwnProperty(key)) {
          state.temporaryPublishingDelayData[key] =
            state.publishingDelayData[key];
        }
      }

      for (const p of PLATFORM_LIST_WITH_ALL) {
        state.postData[p].tempPublicationData.time =
          state.postData[p].planned_publication_datetime_time;

        state.postData[p].tempPublicationData.date =
          state.postData[p].planned_publication_datetime_date;

        state.postData[p].tempPublicationData.datetime =
          state.postData[p].planned_publication_datetime;
      }
    },
    setShowPostIdeaModal: (state, action: PayloadAction<boolean>) => {
      state.ui.showPostIdeaModal = action.payload;
    },
    setQuillEditors: (state, action: PayloadAction<any>) => {
      state.quillEditors[action.payload.platform] = action.payload.editor;
    },

    createAnOverlay: (
      state,
      action: PayloadAction<{
        imageUuid: string;
        overlayUuid: string;
        overlayImage: any;
      }>
    ) => {
      const updateItem = state.createdOverlayArray?.find(
        (item) => item?.imageUuid === action.payload.imageUuid
      );

      if (updateItem) {
        const filteredArray = state.createdOverlayArray?.filter(
          (item) => item?.imageUuid !== action.payload.imageUuid
        );
        state.createdOverlayArray = [...filteredArray];
      }
      const newOverlay = {
        imageUuid: action.payload.imageUuid,
        overlayUuid: action.payload.overlayUuid,
        overlayImage: action.payload.overlayImage,
        overlayPreview: '',
        processing: false,
        overlayData: {}
      };

      state.createdOverlayArray = [...state.createdOverlayArray, newOverlay];
    },

    updateAnOverlay: (
      state,
      action: PayloadAction<{
        uuid: string;
        attribute: string;
        value: any;
      }>
    ) => {
      const updateItem = state.createdOverlayArray?.find(
        (item) => item?.imageUuid === action.payload.uuid
      );

      if (updateItem) {
        updateItem[action.payload.attribute] = action.payload.value;
      }
    },

    copyAnOverlay: (
      state,
      action: PayloadAction<{
        oldUuid: string;
        newUuid: string;
      }>
    ) => {
      const copiedItem = state.createdOverlayArray?.find(
        (item) => item?.imageUuid === action.payload.oldUuid
      );

      if (copiedItem) {
        state.createdOverlayArray = [
          ...state.createdOverlayArray,
          { ...copiedItem, imageUuid: action.payload.newUuid }
        ];
      }
    },

    saveOverlays: (state) => {
      const allOverlayData = state.createdOverlayArray?.map(
        (item) => item?.overlayData
      );

      if (allOverlayData) {
        state.overlayData = allOverlayData;
      }
    },
    deleteAnOverlay: (
      state,
      action: PayloadAction<{
        uuid: string;
      }>
    ) => {
      const updateItem = state.createdOverlayArray?.find(
        (item) => item?.imageUuid === action.payload.uuid
      );

      if (updateItem) {
        const filteredArray = state.createdOverlayArray?.filter(
          (item) => item?.imageUuid !== action.payload.uuid
        );
        const filteredOverlayArr = state.overlayData?.filter(
          (item) => item?.image !== action.payload.uuid
        );
        state.createdOverlayArray = [...filteredArray];
        state.overlayData = [...filteredOverlayArr];
      }
    },

    deleteAllOverlays: (state) => {
      state.createdOverlayArray = [];
      state.overlayData = [];
    },

    enableOverlay: (state, action: PayloadAction<{ value: boolean }>) => {
      state.overlay_enabled = action.payload.value;
    },
    updateAiAssistantProfileAttribute: (
      state,
      action: PayloadAction<{
        attribute: string;
        value: any;
      }>
    ) => {
      state.aiAssistant.profile[action.payload.attribute] =
        action.payload.value;
    },
    updateAiAssistantUiAttribute: (
      state,
      action: PayloadAction<{
        attribute: string;
        value: any;
      }>
    ) => {
      state.aiAssistant.ui[action.payload.attribute] = action.payload.value;
    },
    updateAiassistantPendingMessages: (state, action: PayloadAction<any>) => {
      state.aiAssistant.conversation.pendingMessages = action.payload;
    },
    updateAiAssistantMessages: (state, action: PayloadAction<any>) => {
      state.aiAssistant.conversation.messages = action.payload;
    },
    setCurrentMessage: (state, action: PayloadAction<any>) => {
      state.aiAssistant.conversation.currentMessage = action.payload;
    },
    setConversationAttributes: (state, action: PayloadAction<any>) => {
      state.aiAssistant.conversation = {
        ...state.aiAssistant.conversation,
        ...action.payload
      };
    },
    setmessageSettingsAttributes: (state, action: PayloadAction<any>) => {
      state.aiAssistant.conversation.messageSettings = {
        ...state.aiAssistant.conversation.messageSettings,
        ...action.payload
      };
    },
    setShowAiAssistant: (state, action: PayloadAction<boolean>) => {
      state.ui.showAiAssistant = action.payload;
    },
    setShowPostBoost: (state, action: PayloadAction<boolean>) => {
      state.ui.showPostBoost = action.payload;
    },
    updatePostBoostUiAttribute: (
      state,
      action: PayloadAction<{
        attribute: string;
        value: any;
      }>
    ) => {
      state.postBoost.ui[action.payload.attribute] = action.payload.value;
    },
    updatePostBoostLoading: (state, action: PayloadAction<boolean>) => {
      state.postBoost.loading = action.payload;
    },
    setSameBoostForAll: (state, action: PayloadAction<boolean>) => {
      state.postBoost.ui.sameBoostForAll = action.payload;
    },
    updatePostBoostConfig: (
      state,
      action: PayloadAction<{
        platform: string;
        attribute: string;
        value: any;
      }>
    ) => {
      if (!state.postBoost.boost_config[action.payload.platform]) {
        state.postBoost.boost_config[action.payload.platform] = {};
      }
      state.postBoost.boost_config[action.payload.platform][
        action.payload.attribute
      ] = action.payload.value;
    },
    clearInactiveBoostConfigs: (state) => {
      // Clean up boost configurations for platforms that are disabled (active: false)
      Object.keys(state.postBoost.boost_config).forEach((platform) => {
        const config = state.postBoost.boost_config[platform];
        if (config && config.active === false) {
          // Keep only the active: false flag, remove all other data
          state.postBoost.boost_config[platform] = { active: false };
        }
      });
    },
    deleteBoostConfig: (
      state,
      action: PayloadAction<{ platforms: string[] }>
    ) => {
      // Delete boost configuration for specified platforms
      action.payload.platforms.forEach((platform) => {
        delete state.postBoost.boost_config[platform];
      });
      console.log(
        '🗑️ deleteBoostConfig - Deleted platforms:',
        action.payload.platforms
      );
    },
    setConfigAlreadySaved: (state, action: PayloadAction<boolean>) => {
      state.postBoost.configAlreadySaved = action.payload;
      console.log('🏷️ setConfigAlreadySaved:', action.payload);
    },
    updateAiSessionID: (state, action: PayloadAction<string>) => {
      state.aiAssistant.sessionID = action.payload;
    },
    updateAiQuotaAttributes: (state, action: PayloadAction<any>) => {
      state.aiAssistant.aIQuota = {
        ...state.aiAssistant.aIQuota,
        ...action.payload
      };
    },
    aiAssistantToggleMainActionUrlField: (state) => {
      state.aiAssistant.ui.showMainActionUrlInput =
        !state.aiAssistant.ui.showMainActionUrlInput;
    },
    aiAssistantActivateMainTextField: (state) => {
      state.aiAssistant.ui.focusMainTextField = new Date().getTime();
    },
    updatePostApproval: (state, action: PayloadAction<any>) => {
      state.approval_process = action.payload;
    },
    updateTemplateBlob: (
      state,
      action: PayloadAction<{
        uuid: string;
        blob: any;
      }>
    ) => {
      const uuidBlob = state.renderedTemplateImageBlob?.find(
        (item) => item?.uuid === action.payload.uuid
      );

      if (!uuidBlob) {
        state.renderedTemplateImageBlob = [
          ...state.renderedTemplateImageBlob,
          { uuid: action.payload.uuid, blob: action.payload.blob }
        ];
      }
    },
    updateCampaignList: (
      state,
      action: PayloadAction<{
        campaigns: CampaignType[];
      }>
    ) => {
      state.campaigns = action.payload.campaigns;
    }
  }
});

export const {
  setQuillEditors,
  setShowPostIdeaModal,
  updatePlatformEnabled,
  isPlatformEnabled,
  isPlatformSupported,
  updatePostTypeSelected,
  updateUseSeparateMediaFiles,
  updateUseSeparatePublicationDateTime,
  addMediaFiles,
  reorderMediaFiles,
  addMediaUploadingFiles,
  clearUnuploadedFiles,
  addDraggedFiles,
  removeDraggedFiles,
  addMediaLibraryFiles,
  removeMediaLibraryFiles,
  deleteAfile,
  deleteALoadingfile,
  fileUploadStatus,
  addPublicationDate,
  addPublicationTime,
  clearPublicationDateTime,
  addPublicationDateTime,
  addMainPublicationDateTime,
  addTime,
  addPublicationDelay,
  addRecurringStr,
  updatePublishingDelayData,
  clearRecurrenceData,
  clearPublishingDelayData,
  updateRecurringStatus,
  updateUseSeparateTextFields,
  updatePostText,
  addCreatorText,
  addImageThumbnail,
  editImageThumbnail,
  clearImageThumbnail,
  cancelImageThumbnail,
  layerImageThumbnail,
  updateUploadedImagePresets,
  updateLayeredImagePresets,
  updateLayeredImageState,
  updateEditedImageState,
  updateEditorImagePresets,
  updateEditedImagePresets,
  saveEditorFullImage,
  updateThumbnailProcessing,
  updateMediaAssetFile,
  replaceMediaAssetFile,
  toggleActivePlatform,
  updatePlatformSelectionFilter,
  removeActivePlatform,
  updateTagSelectionFilter,
  addActivePlatform,
  setShowOnlySelectedPlatforms,
  setUploadAreaVisibility,
  resetActiveLocations,
  setShowProfileGroupSelectModal,
  setScheduleModalToggle,
  setShowEditImageModal,
  setGeneralPostType,
  setPlatformFirstComment,
  setSliceAttribute,
  setPostDataAttribute,
  updateMultiImageEditorCloseState,
  updateEditorCloseState,
  updateBrandEditorCloseState,
  updateOverlayCreatorCloseState,
  updateThumbnailCloseState,
  updateEditorUpdateState,
  updateIGTagModalCloseState,
  updateIGProductTagModalCloseState,
  updateIGTags,
  updateProductIGTags,
  updateProductCatalog,
  deleteATag,
  deleteAProductTag,
  addIGCollaborator,
  deleteACollaborator,
  saveCroppedImageCache,
  setMediaDataAttribute,
  updateCancelToken,
  toggleActivePlatformTemp,
  removeActivePlatformTemp,
  addActivePlatformTemp,
  resetActiveLocationsTemp,
  syncProfileGroupSelectState,
  addCropObject,
  switchPreviewMode,
  addUnEditedMediaFiles,
  deleteUnEditedMediafile,
  updateAssetUploadingState,
  clearEditedMediafile,
  setModalUiContent,
  tempToMainScheduleData,
  mainToTempScheduleData,
  updatePostKey,
  updateUrlPreviewData,
  updatePostDataKey,
  createAnOverlay,
  updateAnOverlay,
  copyAnOverlay,
  saveOverlays,
  deleteAnOverlay,
  deleteAllOverlays,
  enableOverlay,
  updateAiAssistantProfileAttribute,
  updateAiAssistantUiAttribute,
  updateAiassistantPendingMessages,
  updateAiAssistantMessages,
  setCurrentMessage,
  setConversationAttributes,
  setmessageSettingsAttributes,
  setShowAiAssistant,
  setShowPostBoost,
  updatePostBoostUiAttribute,
  updatePostBoostLoading,
  setSameBoostForAll,
  updatePostBoostConfig,
  clearInactiveBoostConfigs,
  deleteBoostConfig,
  setConfigAlreadySaved,
  updateAiSessionID,
  updateAiQuotaAttributes,
  aiAssistantToggleMainActionUrlField,
  updatePostApproval,
  aiAssistantActivateMainTextField,
  updateTemplateBlob,
  updateCampaignList
} = lomavisCreatorSlice.actions;
export default lomavisCreatorSlice.reducer;

export const updateMediaAssetFileAsync = createAsyncThunk(
  'media/updateMediaAssetFile',
  async (
    payload: {
      file: any;
      preview_image: string;
      assetBlob: any;
      uuid: string;
      platform: string;
      uploadPresets: any;
      source_media_uuid: any;
      width: string | number;
      height: string | number;
    },
    { dispatch }
  ) => {
    dispatch(updateMediaAssetFile(payload)); // Dispatch the synchronous reducer
    return payload; // Ensure the function returns the correct payload type
  }
);

export const getProfileDataForPlatform = (
  platform: string,
  lomavisCreatorConfig: any
) => {
  const { connected_platforms, platform_display_data } = lomavisCreatorConfig;

  if (!connected_platforms[platform]) {
    return {
      platform: PLATFORM_STATIC_ICONS_PNG[platform],
      name: PLATFORM_LIST_NAMES[platform] || platform,
      id: platform
    };
  }

  const profileData = platform_display_data[platform];
  if (profileData) {
    return {
      platform:
        profileData.profile_image_url || PLATFORM_STATIC_ICONS_PNG[platform],
      name: profileData.display_name || PLATFORM_LIST_NAMES[platform],
      id: platform
    };
  }
};
export const trasnferPostToSliceFormat = (post) => {
  const lomavisCreatorConfig = readLomavisCreatorConfig();
  const effectiveLomavisCreatorConfig =
    post.lomavis_creator_config ?? lomavisCreatorConfig;

  console.log('enabled', post?.enabled_platforms);

  const persistedPlatformEnabled =
    post?.enabled_platforms ||
    PLATFORM_LIST.reduce((acc, platform) => {
      acc[platform] = !!post[`${platform}_post`];
      return acc;
    }, {});

  let general_post_type = POST_TYPE_CHOICES_MAP[post.post_type]
    ? POST_TYPE_CHOICES_MAP[post.post_type]
    : POST_TYPE_CHOICES_MAP[POST_TYPE_CHOICE_POST];

  // This handles the case that a post was created as template post within multi-post
  if (
    post?.overlay?.enabled &&
    effectiveLomavisCreatorConfig?.location?.uuid
  ) {
    general_post_type = POST_TYPE_KEY;
  }

  if (post?.is_template_post && !effectiveLomavisCreatorConfig?.location) {
    general_post_type = POST_TYPE_TEMPLATE;
  }
  if (
    !effectiveLomavisCreatorConfig?.location &&
    post?.lomavisCreatorCacheReload?.is_template_post
  ) {
    general_post_type = POST_TYPE_TEMPLATE;
  }

  const additionalConfig: any = {
    uuid: post.uuid || '',
    post_status: post.post_status || null,
    post_sub_status: post.post_sub_status || null,
    use_individual_images: post.use_individual_images || false,
    use_separate_publication_datetime:
      post.use_separate_publication_datetime || false,
    use_separate_text: post.use_separate_text || false,
    useSeparateMediaFiles: post.use_individual_images || false,
    useSeparateTextFields: post.use_separate_text || false,
    creator_multipost_config: {},
    recurrence_as_str: post.recurrence_as_str || '',
    is_recurring_post: post.is_recurring_post || false,
    is_template_post: post.is_template_post || false,
    lomavisCreatorConfig: effectiveLomavisCreatorConfig,
    persistedPlatformEnabled,
    platformEnabled: {},
    postIdea: post.post_idea || null,
    general_post_type: general_post_type,
    has_been_published: post.has_been_published || false,
    all_platforms_publicaton_datetime: null,
    created_in_lomavis: post.created_in_lomavis || false,
    approval_process: post.approval_process || null,

    tempState: {},
    campaigns: post?.campaigns
  };
  additionalConfig.platformEnabled = PLATFORM_LIST.reduce((acc, platform) => {
    const platformIsAvailableInCurrentScope =
      !!effectiveLomavisCreatorConfig?.connected_platforms?.[platform] &&
      !!effectiveLomavisCreatorConfig?.supported_platforms?.[platform];

    acc[platform] =
      platformIsAvailableInCurrentScope && !!persistedPlatformEnabled[platform];
    return acc;
  }, {});

  if (
    post.creator_multipost_config &&
    effectiveLomavisCreatorConfig.is_multipost &&
    effectiveLomavisCreatorConfig.is_update
  ) {
    const creator_multipost_config_as_json = JSON.parse(
      post.creator_multipost_config
    );
    additionalConfig.creator_multipost_config =
      creator_multipost_config_as_json;

    additionalConfig.tempState.creator_multipost_config =
      creator_multipost_config_as_json;

    additionalConfig.multipost_copied_to_profile_group_for_approval =
      post.multipost_use_instagram_default_location;
    additionalConfig.multipost_copied_to_profile_group_for_approval =
      post.multipost_copied_to_profile_group_for_approval;

    additionalConfig.publishingDelayData = JSON.parse(
      post.publication_delay_config || '{}'
    );
    additionalConfig.temporaryPublishingDelayData = JSON.parse(
      post.publication_delay_config || '{}'
    );
  }

  const postData = {};

  PLATFORM_LIST_WITH_ALL.forEach((platform) => {
    const postKey = `${platform}_post`;

    if (post[postKey]) {
      postData[platform] = {
        ...post[postKey],
        uuid: post[postKey]?.uuid || '',
        text: JSON.parse(post[postKey]?.text || '{}'),
        first_comment: JSON.parse(post[postKey]?.first_comment || '{}'),
        instagram_location_id: post[postKey]?.instagram_location_id || null,
        instagram_location_name: post[postKey]?.instagram_location_name || null,
        facebook_location_name: post[postKey]?.facebook_location_name || null,
        facebook_location_id: post[postKey]?.facebook_location_id || null,
        instagram_user_tags: post[postKey]?.instagram_user_tags || [],
        instagram_temp_tags: post[postKey]?.media
          ?.map((obj, index) => {
            if (post[postKey]?.instagram_user_tags.length > 0) {
              const allTags = JSON.parse(post[postKey]?.instagram_user_tags);
              const tags = allTags[index];

              if (tags && tags.length > 0) {
                return { uuid: obj.uuid, tags: tags };
              }
            }
          })
          .filter(Boolean),

        selected_instagram_catalog: post[postKey]?.selected_instagram_catalog,

        youtube_title: post[postKey]?.youtube_title || '',
        youtube_tags: post[postKey]?.youtube_tags
          ? JSON.parse(post[postKey].youtube_tags)
          : [],

        youtube_category: post[postKey]?.youtube_category || 0,
        youtube_privacy_status:
          post[postKey]?.youtube_privacy_status || 'public',
        youtube_available_for_kids:
          post[postKey]?.youtube_available_for_kids || 0,
        planned_publication_datetime:
          post[postKey]?.planned_publication_datetime || null,
        google_cta_type: post[postKey]?.google_cta_type || null,
        google_cta_url: post[postKey]?.google_cta_url || null,
        google_post_type: post[postKey]?.google_post_type || 'standard',
        google_post_event_start_datetime:
          post[postKey]?.google_post_event_start_datetime || null,
        google_post_event_end_datetime:
          post[postKey]?.google_post_event_end_datetime || null,
        google_post_event_title: post[postKey]?.google_post_event_title || null,

        video_thumbnail: post[postKey]?.video_thumbnail,
        video_thumbnail_editor: post[postKey]?.video_thumbnail_editor,
        video_thumbnail_background_image:
          post[postKey]?.video_thumbnail_background_image,

        uploadedImageThumbnail: {
          url:
            Object.keys(post[postKey]?.video_thumbnail || {}).length > 0 &&
            (post[postKey]?.video_thumbnail_background_image
              ? post[postKey]?.video_thumbnail_background_image
              : post[postKey]?.media
              ? post[postKey]?.media[0]?.preview_image
              : ''),
          file: null,
          uploadPresets: null,
          uploaded: true
        },
        editedImageThumbnail: {
          url: post[postKey]?.video_thumbnail_editor?.original_asset,
          file: null,
          uploadPresets: post[postKey]?.video_thumbnail_editor?.uploadPresets,
          uploaded: false,
          media: post[postKey]?.video_thumbnail_editor
        },
        fullEditorImage: {
          url: post[postKey]?.video_thumbnail_editor?.original_asset,
          file: null,
          uploadPresets: null,
          uploaded: true,
          currentCrop: null
        },
        layeredImageThumbnail: {
          file: post[postKey]?.video_thumbnail?.original_asset,
          uploadPresets: post[postKey].video_thumbnail?.uploadPresets,
          uploaded: false,
          media: post[postKey].video_thumbnail
        },

        [`${POST_TYPE_TO_PREFIX[general_post_type]}_tags`]:
          fromArrayStringToArr(
            post[postKey][`${POST_TYPE_TO_PREFIX[general_post_type]}_tags`] ||
              ''
          ),

        media:
          post[postKey]?.media?.map((item) => ({
            media_type: item.media_type,
            file_type: item.file_type,
            preview_image: item.preview_image,
            image_url: item.preview_image,
            uuid: item.uuid,
            name: item?.name,
            thumbnail_image: item.thumbnail_image,
            original_asset: item.original_asset,
            preview: item.preview_image,
            video_duration: item.video_duration,
            video_audio_channels: item.video_audio_channels,
            file_size: item.original_filesize_bytes,
            width: item.width,
            height: item.height,
            contentType: item.content_type,
            source_media_uuid: item.source_media_uuid,
            deleteUrl: item.delete_url,
            uploaded: true,
            isUploading: false,
            newUpload: false,
            type: item.media_type,
            editorUpdateState: false,
            id: item.uuid,
            uploadPreset: item.uploadPreset,
            media_alt_description: item.media_alt_description,
            previewImageURL: item.previewImageURL,
            saved_in_media_library: item.saved_in_media_library,
            api_url: item.api_url
          })) || [],
        cropList: post[postKey]?.crop_list || []
      };
      postData[ALL] = { ...postData[platform] };

      if (!post.use_separate_text) {
        postData[ALL].text = JSON.parse(post.all_platforms_text || '{}');
      }
    }
  });

  PLATFORM_LIST_WITH_ALL.forEach((platform) => {
    const postKey = `${platform}_post`;
    if (postData[platform]) {
      // For ALL we usually rely on all_platforms_text, not on a non‑existent
      // `all_post` key. Avoid overwriting previously set text with `{}` when
      // that key is missing.
      if (postKey === 'all_post' && !post[postKey]) {
        return;
      }

      const incomingText = post[postKey]?.text;
      if (incomingText) {
        try {
          postData[platform].text = JSON.parse(incomingText);
        } catch (err) {
          console.error('Failed to parse saved text for', platform, err);
        }
      }
    }
  });

  // After computing additionalConfig and postData…

  // Collect enabled platforms and their derived post types
  const enabledPlatforms = Object.keys(additionalConfig.platformEnabled).filter(
    (p) => additionalConfig.platformEnabled[p]
  );

  // Get each platform's post type
  const platformPostTypes = enabledPlatforms.map((p) => {
    const postKey = `${p}_post`;
    const pData = post[postKey];

    if (!pData) return null;

    const isInstagramTrial =
      pData?.additional_publication_data?.instagram_trial_reel_enabled ||
      pData?.additional_publication_data?.instagram_graduation_strategy;

    if (pData.is_facebook_story || pData.is_instagram_story) {
      return POST_TYPE_STORY_KEY;
    }
    if (isInstagramTrial) {
      return POST_TYPE_TRIAL_REEL_KEY;
    }
    if (pData.is_facebook_reel || pData.is_instagram_reel) {
      return POST_TYPE_REEL_KEY;
    }
    // fallback: treat as generic
    return additionalConfig.general_post_type;
  });

  // If all selected platforms are story → general_post_type = story
  if (
    platformPostTypes.length > 0 &&
    platformPostTypes.every((t) => t === POST_TYPE_STORY_KEY)
  ) {
    additionalConfig.general_post_type = POST_TYPE_STORY_KEY;
  }

  // If all selected platforms are trial reel → general_post_type = trial reel
  if (
    platformPostTypes.length > 0 &&
    platformPostTypes.every((t) => t === POST_TYPE_TRIAL_REEL_KEY)
  ) {
    additionalConfig.general_post_type = POST_TYPE_TRIAL_REEL_KEY;
  }

  // If all selected platforms are reel → general_post_type = reel
  if (
    platformPostTypes.length > 0 &&
    platformPostTypes.every((t) => t === POST_TYPE_REEL_KEY)
  ) {
    additionalConfig.general_post_type = POST_TYPE_REEL_KEY;
  }

  additionalConfig.postTypeSelected =
    post.postTypeSelected ??
    generatePostTypeMap(post, additionalConfig.general_post_type);

  const overlayInformation = {
    overlayData: post?.overlayData || post?.overlay?.overlays || [],

    createdOverlayArray:
      post?.overlay?.overlays?.map((item, index) => {
        const newOverlay = {
          imageUuid: item?.image,
          overlayUuid: item?.overlay?.uuid,
          overlayImage: item?.overlay?.url,
          overlayPreview: '',
          processing: false,
          overlayData: item
        };

        return newOverlay;
      }) || [],

    overlay_enabled: post?.overlay_enabled || post?.overlay?.enabled || false
  };
  // add ai assistant data
  if (post.ai_assistant) {
    additionalConfig.aiAssistant = { ...aiAssistantDefaultState };
    additionalConfig.aiAssistant.conversation = {
      ...aiAssistantDefaultState.conversation,
      messages: [...post.ai_assistant.messages], // Create a new array for messages
      count: post.ai_assistant.count
    };
  }

  // Load post boost config from additional_publication_data
  let postBoostData: any = {};
  if (post.post_boost_config) {
    // Check if it's stored at the global level
    postBoostData = post.post_boost_config;
  } else {
    // Check if it's stored in any platform's additional_publication_data
    // and reconstruct the full boost_config from individual platforms
    let reconstructedBoostConfig = {};
    let foundUiState = null;

    for (const platform of PLATFORM_LIST_WITH_ALL) {
      const postKey = `${platform}_post`;
      const platformBoostData =
        post[postKey]?.additional_publication_data?.post_boost_config;

      if (platformBoostData) {
        // Extract platform-specific config (excluding sameBoostForAll)
        const { sameBoostForAll, ...platformConfig } = platformBoostData;
        reconstructedBoostConfig[platform] = platformConfig;

        // Store UI state (should be same across platforms)
        if (sameBoostForAll !== undefined && !foundUiState) {
          foundUiState = { sameBoostForAll };
        }
      }
    }

    if (Object.keys(reconstructedBoostConfig).length > 0) {
      postBoostData = {
        boost_config: reconstructedBoostConfig,
        ui: foundUiState || { sameBoostForAll: true }
      };
    }
  }

  // Handle both old format (direct boost_config) and new format (with ui)
  let boostConfig = {};
  let uiState = { sameBoostForAll: true }; // default value

  if (postBoostData.boost_config && postBoostData.ui) {
    // New format: { boost_config: {...}, ui: { sameBoostForAll: boolean } }
    boostConfig = postBoostData.boost_config;
    uiState = postBoostData.ui;
  } else if (
    postBoostData.boost_config &&
    postBoostData.sameBoostForAll !== undefined
  ) {
    // Transition format: { boost_config: {...}, sameBoostForAll: boolean }
    boostConfig = postBoostData.boost_config;
    uiState = { sameBoostForAll: postBoostData.sameBoostForAll };
  } else if (
    typeof postBoostData === 'object' &&
    !postBoostData.boost_config &&
    !postBoostData.ui
  ) {
    // Old format: direct boost config object
    boostConfig = postBoostData;
  }

  additionalConfig.postBoost = {
    ...postBoostDefaultState,
    boost_config: boostConfig,
    ui: uiState
  };

  return {
    postData,
    ...additionalConfig,
    ...overlayInformation
  };
};

export const loadPost = (post) => (dispatch) => {
  const lomavisCreatorConfig = readLomavisCreatorConfig();

  const parsedPost = trasnferPostToSliceFormat(post);

  dispatch(lomavisCreatorSlice.actions.loadPostToState(parsedPost));

  if (lomavisCreatorConfig.is_update) {
    for (const platform of PLATFORM_LIST_WITH_ALL) {
      const postKey = `${platform}_post`;
      if (post[postKey]) {
        const datetime = post[postKey].planned_publication_datetime;
        if (datetime) {
          const timezone = lomavisCreatorConfig?.timezone;
          const datetimeWithTimezone = moment
            .utc(datetime)
            .tz(timezone)
            .format();
          console.log('datetimeWithTimezone', datetimeWithTimezone);

          dispatch(
            addMainPublicationDateTime({
              platform,
              dateTime: datetimeWithTimezone
            })
          );
        }
      }
    }
  }

  // Update selected post type for each platform
  for (const platform of PLATFORM_LIST) {
    const postKey = `${platform}_post`;
    if (post[postKey]) {
      const instagramFlags = post[postKey]?.additional_publication_data || {};
      const hasInstagramTrial =
        platform === INSTAGRAM &&
        Boolean(
          instagramFlags.instagram_trial_reel_enabled ||
            instagramFlags.instagram_graduation_strategy
        );

      // check if platform general post type exist
      if (
        parsedPost.general_post_type &&
        platformFeatures[platform] &&
        platformFeatures[platform][parsedPost.general_post_type]
      ) {
        for (const index in platformFeatures[platform][parsedPost.general_post_type]) {
          const configObject =
            platformFeatures[platform][parsedPost.general_post_type][index];
          const dataFieldEnable = configObject?.dataFieldEnable?.[0];
          if (!dataFieldEnable) continue;

          if (post[postKey][dataFieldEnable] == true) {
            // For loaded Trial Reels, skip the plain Reel transition because it clears
            // trial flags/strategy in updatePostTypeSelected before Trial Reel is applied.
            if (
              platform === INSTAGRAM &&
              hasInstagramTrial &&
              configObject.typeKey === POST_TYPE_REEL_KEY
            ) {
              continue;
            }

            // Avoid misclassifying normal Instagram Reels as Trial Reels.
            if (
              platform === INSTAGRAM &&
              configObject.typeKey === POST_TYPE_TRIAL_REEL_KEY &&
              !hasInstagramTrial
            ) {
              continue;
            }

            dispatch(
              updatePostTypeSelected({
                platform,
                postType: configObject.typeKey
              })
            );
          }
        }
      }

      // Special handling: Instagram trial reel -> prefer trial type if trial flags present
      if (hasInstagramTrial) {
        dispatch(
          updatePostTypeSelected({
            platform,
            postType: POST_TYPE_TRIAL_REEL_KEY
          })
        );
      }
    }
  }
};
export const transferDataToApiFormat = (
  lomavisCreator,
  action = null,
  locations = []
) => {
  console.log('transferDataToApiFormat', lomavisCreator);

  // Clean up inactive boost configurations before preparing API data
  const cleanedLomavisCreator = {
    ...lomavisCreator,
    postBoost: {
      ...lomavisCreator.postBoost,
      boost_config: { ...lomavisCreator.postBoost.boost_config }
    }
  };

  // normalize general post type to handle ree and story type and prevent issues
  const normalizedGeneralType = [
    POST_TYPE_STORY_KEY,
    POST_TYPE_REEL_KEY,
    POST_TYPE_TRIAL_REEL_KEY
  ].includes(cleanedLomavisCreator.general_post_type)
    ? POST_TYPE_KEY
    : cleanedLomavisCreator.general_post_type;
  // Clean up boost configurations for platforms that are disabled (active: false)
  Object.keys(cleanedLomavisCreator.postBoost.boost_config).forEach(
    (platform) => {
      const config = cleanedLomavisCreator.postBoost.boost_config[platform];
      if (config && config.active === false) {
        // Keep only the active: false flag, remove all other data
        cleanedLomavisCreator.postBoost.boost_config[platform] = {
          active: false
        };
      }
    }
  );

  // transfer post data to api accepted format
  const postDataToSend: any = {};
  // adding selected Post Types:
  postDataToSend.postTypeSelected = cleanedLomavisCreator?.postTypeSelected;
  PLATFORM_LIST_WITH_ALL.forEach((platform) => {
    const postKey = `${platform}_post`;
    if (cleanedLomavisCreator?.postData[platform]) {
      if (
        cleanedLomavisCreator?.postData[platform]?.media?.length > 0 ||
        cleanedLomavisCreator?.postData[platform]?.text
      )
        if (cleanedLomavisCreator?.platformEnabled[platform]) {
          postDataToSend[postKey] = {
            ...cleanedLomavisCreator?.postData?.[platform]
          };

          // Add post boost configuration to additional_publication_data
          if (
            cleanedLomavisCreator?.postBoost?.boost_config &&
            Object.keys(cleanedLomavisCreator.postBoost.boost_config).length > 0
          ) {
            const platformBoostConfig =
              cleanedLomavisCreator.postBoost.boost_config[platform];
            if (platformBoostConfig) {
              postDataToSend[postKey].additional_publication_data = {
                ...postDataToSend[postKey].additional_publication_data,
                post_boost_config: {
                  ...platformBoostConfig,
                  sameBoostForAll:
                    cleanedLomavisCreator.postBoost.ui.sameBoostForAll
                }
              };
            }
          }

          postDataToSend[postKey][
            `${
              POST_TYPE_TO_PREFIX[normalizedGeneralType]
            }_tags`
          ] = fromArrayToArrString(
            cleanedLomavisCreator.postData[platform][
              `${
                POST_TYPE_TO_PREFIX[normalizedGeneralType]
              }_tags`
            ] || []
          );

          // adjust media
          postDataToSend[postKey].media = postDataToSend[postKey].media.map(
            (media) => {
              if (!media.uploadPreset) return media;
              const newMedia = { ...media };
              newMedia.preview =
                newMedia?.uploadPreset?.uploaded_object_data.preview_image_url;
              newMedia.preview_image =
                newMedia?.uploadPreset?.uploaded_object_data.preview_image_url;
              newMedia.image_url =
                newMedia?.uploadPreset?.uploaded_object_data.preview_image_url;
              return newMedia;
            }
          );

          postDataToSend[postKey].video_thumbnail_background_image = null;

          // stringify the text
          postDataToSend[postKey].text = JSON.stringify(
            cleanedLomavisCreator?.postData?.[platform]?.text
          );

          postDataToSend[postKey].crop_list = postDataToSend[postKey]?.cropList;

          postDataToSend[postKey].first_comment = cleanedLomavisCreator
            ?.postData?.[platform]?.first_comment
            ? JSON.stringify(
                cleanedLomavisCreator?.postData?.[platform]?.first_comment
              )
            : null;

          postDataToSend[postKey].youtube_tags = JSON.stringify(
            cleanedLomavisCreator?.postData?.[platform]?.youtube_tags
          );
          // Remove null values, except for fields that intentionally use null
          // to signal a semantic action (e.g. planned_publication_datetime:
          // null clears a previously scheduled publication time).
          const nullAllowlist = ['planned_publication_datetime'];
          for (const key in postDataToSend[postKey]) {
            if (
              postDataToSend[postKey][key] === null &&
              !nullAllowlist.includes(key)
            ) {
              delete postDataToSend[postKey][key];
            }
          }

          // this moves the temp intagram tags to the

          let initialTagArray = Array(10).fill([]);

          cleanedLomavisCreator?.postData?.[
            platform
          ]?.instagram_temp_tags?.forEach((obj) => {
            // Find the index of the media file corresponding to the tag's uuid
            const itemIndex = postDataToSend[postKey].media.findIndex(
              (item) => item?.uuid === obj?.uuid
            );

            // Ensure the index is valid (i.e., media item with that uuid exists)
            if (itemIndex !== -1) {
              // Assign the tags to the corresponding index in the initialTagArray
              initialTagArray[itemIndex] = obj.tags;
            }
          });

          postDataToSend[postKey].instagram_user_tags =
            JSON.stringify(initialTagArray);
          postDataToSend[postKey].instagram_user_tags =
            JSON.stringify(initialTagArray);
        }
    }
  });
  // add other keys
  postDataToSend.use_individual_images =
    cleanedLomavisCreator.useSeparateMediaFiles;
  postDataToSend.use_separate_publication_datetime =
    cleanedLomavisCreator.use_separate_publication_datetime;
  postDataToSend.use_separate_text =
    cleanedLomavisCreator.useSeparateTextFields;

  postDataToSend.publication_delay_config = JSON.stringify(
    cleanedLomavisCreator.publishingDelayData
  );

  postDataToSend.creator_multipost_config = JSON.stringify(
    cleanedLomavisCreator.creator_multipost_config
  );
  postDataToSend.recurrence_as_str = cleanedLomavisCreator.recurrence_as_str;
  postDataToSend.multipost_use_instagram_default_location =
    cleanedLomavisCreator.multipost_use_instagram_default_location;
  postDataToSend.multipost_copied_to_profile_group_for_approval =
    cleanedLomavisCreator.multipost_copied_to_profile_group_for_approval;
  postDataToSend.is_recurring_post = cleanedLomavisCreator.is_recurring_post;
  postDataToSend.lomavis_creator_config =
    cleanedLomavisCreator.lomavisCreatorConfig;
  postDataToSend.action = action ? ACTIONS_VALUES_MAP[action] : null;

  const visiblePlatformEnabled = PLATFORM_LIST.reduce((acc, platform) => {
    const platformIsAvailableInCurrentScope =
      !!cleanedLomavisCreator?.lomavisCreatorConfig?.connected_platforms?.[
        platform
      ] &&
      !!cleanedLomavisCreator?.lomavisCreatorConfig?.supported_platforms?.[
        platform
      ];

    if (platformIsAvailableInCurrentScope) {
      acc[platform] = cleanedLomavisCreator.platformEnabled[platform];
    }

    return acc;
  }, {});

  postDataToSend.enabled_platforms = {
    ...(cleanedLomavisCreator.persistedPlatformEnabled || {}),
    ...visiblePlatformEnabled
  };
  postDataToSend.is_multipost =
    cleanedLomavisCreator.lomavisCreatorConfig.is_multipost;

  postDataToSend.cloud_uuid =
    cleanedLomavisCreator.lomavisCreatorConfig?.cloud?.uuid;
  postDataToSend.location_uuid =
    cleanedLomavisCreator.lomavisCreatorConfig?.location?.uuid;

  postDataToSend.all_platforms_text = JSON.stringify(
    cleanedLomavisCreator.postData[ALL].text
  );

  postDataToSend.approval_process =
    cleanedLomavisCreator?.approval_process || null;

  // Preserve whether the user has overlays turned on; avoid forcing it to true on reloads/cache saves.
  postDataToSend.overlay = {
    enabled: cleanedLomavisCreator?.overlay_enabled ?? false,
    overlays: cleanedLomavisCreator?.overlayData
  };

  // Add post boost configuration
  if (
    cleanedLomavisCreator?.postBoost?.boost_config &&
    Object.keys(cleanedLomavisCreator.postBoost.boost_config).length > 0
  ) {
    postDataToSend.post_boost_config = {
      boost_config: cleanedLomavisCreator.postBoost.boost_config,
      ui: cleanedLomavisCreator.postBoost.ui
    };
  } else {
    postDataToSend.post_boost_config = {
      boost_config: {},
      ui: cleanedLomavisCreator?.postBoost?.ui || { sameBoostForAll: true }
    };
  }

  // Log boost config for debugging
  if (
    cleanedLomavisCreator?.postBoost?.boost_config &&
    Object.keys(cleanedLomavisCreator.postBoost.boost_config).length > 0
  ) {
    console.log(
      'Post boost config being sent:',
      postDataToSend.post_boost_config
    );
  }

  // ----comment this out-------------------

  // add ai assistant data
  postDataToSend.ai_assistant = {
    messages: cleanedLomavisCreator.aiAssistant.conversation.messages,
    count: cleanedLomavisCreator.aiAssistant.conversation.messages.length,
    profile: cleanedLomavisCreator.aiAssistant.profile
  };
  // flip POST_TYPE_CHOICES_MAP to get the key by value

  postDataToSend.post_type = Object.keys(POST_TYPE_CHOICES_MAP).find(
    (key) =>
      POST_TYPE_CHOICES_MAP[key] === normalizedGeneralType
  );

  //This is soecific to template post; when saved it is a post not a template internally.
  // While loading we check if it is a template post then adjust around
  if (postDataToSend.post_type === POST_TYPE_CHOICE_TEMPLATE) {
    postDataToSend.post_type = POST_TYPE_CHOICE_POST;
    postDataToSend['lomavisCreatorCacheReload'] = { is_template_post: true };
  }

  postDataToSend.copied_from_uuid =
    cleanedLomavisCreator.lomavisCreatorConfig?.copied_from;

  if (locations) {
    postDataToSend.creator_multipost_config_metadata = [];

    const enabledLocations = new Set();
    for (const key of Object.keys(
      cleanedLomavisCreator.creator_multipost_config || {}
    )) {
      if (cleanedLomavisCreator.creator_multipost_config[key]) {
        const uuid = key.split('_')[0];
        enabledLocations.add(uuid);
      }
    }

    if (locations) {
      enabledLocations.forEach((enabledLocation) => {
        const location = locations.find((loc) => loc.uuid === enabledLocation);
        if (!location) return;
        postDataToSend.creator_multipost_config_metadata.push({
          uuid: location.uuid,
          name: location.name,
          slug: location.slug
        });
      });
    }
  }

  postDataToSend.created_in_lomavis = cleanedLomavisCreator.created_in_lomavis;
  postDataToSend.created_in_lomavis = lomavisCreator.created_in_lomavis;
  postDataToSend.campaigns = lomavisCreator?.campaigns;

  return postDataToSend;
};

export const sendPostData = async (lomavisCreator, action, locations) => {
  const postDataToSend = transferDataToApiFormat(
    lomavisCreator,
    action,
    locations
  );
  return await new Promise<any>(async (resolve_promise, reject) => {
    try {
      let response;
      if (lomavisCreator.lomavisCreatorConfig?.is_update) {
        response = await axiosClient.put(
          `${lomavisCreator?.lomavisCreatorConfig.http_endpoint}?is_multipost=${
            lomavisCreator?.lomavisCreatorConfig.is_multipost
          }&location=${
            lomavisCreator?.lomavisCreatorConfig.location != null
          }&cloud=${lomavisCreator?.lomavisCreatorConfig.cloud != null}`,
          postDataToSend
        );
      } else {
        response = await axiosClient.post(
          lomavisCreator?.lomavisCreatorConfig.http_endpoint,
          postDataToSend
        );
      }
      resolve_promise(response.data);
    } catch (e) {
      reject(e);
    }
  });
};

// Helper: coerce truthy values (true, 1, "1", "true")
const truthy = (v: any) => v === true || v === 1 || v === '1' || v === 'true';

/**
 * Compute per-platform post type.
 * If generalPostType is STORY or REEL, we force that type for present/enabled platform blocks.
 */
function generatePostTypeMap(
  post: any,
  generalPostType?: string
): Record<string, string> {
  const map: Record<string, string> = {};

  for (const p of PLATFORM_LIST_WITH_ALL) {
    const block = post[`${p}_post`]; // e.g. instagram_post, facebook_post, ...
    const hasBlock = !!block;

    // Handle ALL pseudo-platform cleanly
    if (p === ALL) {
      map[p] = generalPostType ?? POST_TYPE_POST_KEY;
      continue;
    }

    // If you’ve already normalized “all story” / “all reel” / “all trial reel”, honor it here
    if (
      generalPostType === POST_TYPE_STORY_KEY ||
      generalPostType === POST_TYPE_REEL_KEY ||
      generalPostType === POST_TYPE_TRIAL_REEL_KEY
    ) {
      map[p] = hasBlock ? generalPostType : POST_TYPE_POST_KEY;
      continue;
    }

    // Per-platform detection from platform blocks (fallback to root if needed)
    switch (p) {
      case INSTAGRAM: {
        const isStory =
          truthy(block?.is_instagram_story) || truthy(post?.is_instagram_story);
        const isReel =
          truthy(block?.is_instagram_reel) || truthy(post?.is_instagram_reel);
        const isTrial =
          truthy(
            block?.additional_publication_data?.instagram_trial_reel_enabled
          ) ||
          truthy(
            block?.additional_publication_data?.instagram_graduation_strategy
          );

        map[p] = isStory
          ? POST_TYPE_STORY_KEY
          : isTrial
          ? POST_TYPE_TRIAL_REEL_KEY
          : isReel
          ? POST_TYPE_REEL_KEY
          : POST_TYPE_POST_KEY;
        break;
      }

      case FACEBOOK: {
        const isStory =
          truthy(block?.is_facebook_story) || truthy(post?.is_facebook_story);
        const isReel =
          truthy(block?.is_facebook_reel) || truthy(post?.is_facebook_reel);

        map[p] = isStory
          ? POST_TYPE_STORY_KEY
          : isReel
          ? POST_TYPE_REEL_KEY
          : POST_TYPE_POST_KEY;
        break;
      }

      case YOUTUBE: {
        const isShort =
          truthy(block?.is_youtube_short) || truthy(post?.is_youtube_short);

        map[p] = isShort ? POST_TYPE_SHORT_VIDEO_KEY : POST_TYPE_VIDEO_KEY;
        break;
      }

      case WHATSAPP: {
        const isStatus =
          truthy(block?.is_whatsapp_status) || truthy(post?.is_whatsapp_status);

        map[p] = isStatus ? POST_TYPE_STATUS : POST_TYPE_MESSAGE;
        break;
      }

      // Add other platforms here if you support special types:
      // case TIKTOK: ...
      // case PINTEREST: ...
      // case LINKEDIN: ...

      default: {
        // Sensible default
        map[p] = POST_TYPE_POST_KEY;
        break;
      }
    }
  }

  return map;
}

export const saveAiAssistantProfile =
  (callback) => async (dispatch: any, getState: any) => {
    const profileSettings = {
      ...getState().lomavisCreator.aiAssistant.profile
    };
    const url = resolve(
      'lomavis_ai_settings',
      { uuid: profileSettings.uuid },
      {}
    );
    // adjust home page ( if it doesn't have a protocol add it )
    if (profileSettings.homepage) {
      if (!profileSettings.homepage.startsWith('https')) {
        profileSettings.homepage = `https://${profileSettings.homepage}`;
      }
    }
    try {
      await axiosClient.put(url, profileSettings);
      // update the profile settings also to messageSettings
      dispatch(
        setmessageSettingsAttributes({
          tone: profileSettings.tone,
          language: profileSettings.language,
          platform: profileSettings.platform
        })
      );
      callback();
    } catch (err: any) {
      console.log(err);
    }
  };

export const sendAiAssistantMessage =
  () => async (dispatch: any, getState: any) => {
    const url = resolve('lomavis_ai_messages', {}, {});
    try {
      // update isWaitingForResponse
      dispatch(setConversationAttributes({ isWaitingForResponse: true }));
      const message =
        getState().lomavisCreator.aiAssistant.conversation.currentMessage;

      const messageSettings =
        getState().lomavisCreator.aiAssistant.conversation.messageSettings;
      const postUUID = getState().lomavisCreator.uuid;
      const payload = {
        prompt: message,
        additional_data: messageSettings,
        multiplatform_social_media_post: postUUID,
        cloud: getState().lomavisCreator.cloudUuid,
        location: getState().lomavisCreator.locationUuid,
        last_ai_response:
          getState().lomavisCreator.aiAssistant.conversation.messages?.find(
            (msg) => msg.created_by_ai
          )?.uuid,
        session: getState().lomavisCreator.aiAssistant.sessionID
      };
      // remove the payload key if it is empty
      for (const key in payload) {
        if (payload[key] === null || payload[key] === '') {
          delete payload[key];
        }
      }
      // add current message to pending messages
      dispatch(setCurrentMessage(''));
      dispatch(
        updateAiassistantPendingMessages([
          ...getState().lomavisCreator.aiAssistant.conversation.pendingMessages,
          { message, quick_action: false }
        ])
      );

      // get current tone settings and add to message
      const response = await axiosClient.post(url, payload);
      // get old messages list
      const oldMessages =
        getState().lomavisCreator.aiAssistant.conversation.messages;
      // add new message to the list
      // remove pending message
      dispatch(updateAiassistantPendingMessages([]));
      dispatch(updateAiAssistantMessages([...response.data, ...oldMessages]));
      dispatch(
        setConversationAttributes({
          isWaitingForResponse: false,
          count:
            (getState().lomavisCreator.aiAssistant.conversation
              .count as number) + 2
        })
      );
      // x-lomavis-ai-advanced-ai-usage-available

      const aiQuotaAttributes = {
        available_tokens: parseInt(
          response.headers['x-lomavis-ai-available-tokens']
        ),
        used_tokens: parseInt(response.headers['x-lomavis-ai-used-tokens']),
        advanced_available_tokens: parseInt(
          response.headers['x-lomavis-ai-advanced-ai-usage-available']
        ),
        advanced_used_tokens: parseInt(
          response.headers['x-lomavis-ai-advanced-ai-usage-used']
        )
      };
      dispatch(updateAiQuotaAttributes(aiQuotaAttributes));
    } catch (err: any) {
      console.log(err);
      dispatch(setConversationAttributes({ isWaitingForResponse: false }));
    }
  };

export const sendAiAssistantQuickAction =
  (action: any, additionalData = null) =>
  async (dispatch: any, getState: any) => {
    dispatch(setConversationAttributes({ isWaitingForResponse: true }));
    const url = resolve('lomavis_ai_messages', {}, {});
    const actionID = action.id;
    const messageSettings =
      getState().lomavisCreator.aiAssistant.conversation.messageSettings;
    const postUUID = getState().lomavisCreator.uuid;
    const payload = {
      additional_data: {},
      quick_action: actionID,
      multiplatform_social_media_post: postUUID,
      cloud: getState().lomavisCreator.cloudUuid,
      location: getState().lomavisCreator.locationUuid,
      last_ai_response:
        getState().lomavisCreator.aiAssistant.conversation.messages?.find(
          (msg) => msg.created_by_ai
        )?.uuid,
      session: getState().lomavisCreator.aiAssistant.sessionID
    };
    if (action.playloadField) {
      payload.additional_data[action.playloadField] =
        messageSettings[action.playloadField];
    }
    if (additionalData) {
      payload.additional_data = {
        ...payload.additional_data,
        ...additionalData
      };
    }
    for (const key in payload) {
      if (payload[key] === null || payload[key] === '') {
        delete payload[key];
      }
    }
    // add current message to pending messages
    dispatch(
      updateAiassistantPendingMessages([
        ...getState().lomavisCreator.aiAssistant.conversation.pendingMessages,
        { message: action.name, quick_action: true }
      ])
    );
    try {
      const response = await axiosClient.post(url, payload);
      // append new message to the list
      const oldMessages =
        getState().lomavisCreator.aiAssistant.conversation.messages;
      // add new message to the list
      dispatch(updateAiAssistantMessages([...response.data, ...oldMessages]));
      // remove pending message
      dispatch(updateAiassistantPendingMessages([]));
      dispatch(
        setConversationAttributes({
          isWaitingForResponse: false,
          count: getState().lomavisCreator.aiAssistant.conversation.count + 2
        })
      );
      const aiQuotaAttributes = {
        available_tokens: parseInt(
          response.headers['x-lomavis-ai-available-tokens']
        ),
        used_tokens: parseInt(response.headers['x-lomavis-ai-used-tokens']),
        advanced_ai_usage_available: parseInt(
          response.headers['x-lomavis-ai-advanced-ai-usage-available']
        ),
        advanced_ai_usage_used: parseInt(
          response.headers['x-lomavis-ai-advanced-ai-usage-used']
        )
      };
      dispatch(updateAiQuotaAttributes(aiQuotaAttributes));
    } catch (err: any) {
      console.log(err);
      dispatch(
        setConversationAttributes({
          isWaitingForResponse: false
        })
      );
    }
  };

export const getAiAssistantMessages =
  () => async (dispatch: any, getState: any) => {
    try {
      const postUUID = getState().lomavisCreator.uuid;
      if (!postUUID) {
        dispatch(setConversationAttributes({ isInitiated: true }));
        return;
      }
      dispatch(setConversationAttributes({ isLoading: true }));
      const url = resolve(
        'lomavis_ai_messages',
        {},
        { multiplatform_social_media_post__uuid: postUUID }
      );

      const response = await axiosClient.get(url);
      dispatch(updateAiAssistantMessages(response.data.results));
      dispatch(
        setConversationAttributes({
          nextPage: response.data.next,
          count: response.data.count
        })
      );
      dispatch(
        setConversationAttributes({ isLoading: false, isInitiated: true })
      );
    } catch (err: any) {
      console.log(err);
      dispatch(setConversationAttributes({ isLoading: false }));
    }
  };

export const getAiAssistantMessagesNextPage =
  () => async (dispatch: any, getState: any) => {
    const url = getState().lomavisCreator.aiAssistant.conversation.nextPage;
    dispatch(setConversationAttributes({ isLoading: true }));
    try {
      const response = await axiosClient.get(url);
      dispatch(
        updateAiAssistantMessages([
          ...getState().lomavisCreator.aiAssistant.conversation.messages,
          ...response.data.results
        ])
      );
      dispatch(
        setConversationAttributes({
          nextPage: response.data.next,
          count: response.data.count,
          isLoading: false
        })
      );
    } catch (err: any) {
      console.log(err);
      dispatch(setConversationAttributes({ isLoading: false }));
    }
  };

export const resetAIConversation =
  () => async (dispatch: any, getState: any) => {
    // check if post has no uuid
    if (!getState().lomavisCreator.uuid) {
      // generate a new session id
      const sessionID = uuidv4();
      dispatch(updateAiSessionID(sessionID));
    } else {
      // add logic to reset the conversation
      const url = resolve('lomavis_ai_messages_bulk_delete', {}, {});
      try {
        await axiosClient.post(url, {
          multiplatform_social_media_post: getState().lomavisCreator.uuid
        } as any);
      } catch (err: any) {
        console.log(err);
      }
    }
    // reset the conversation
    dispatch(setConversationAttributes({ messages: [], count: 0 }));
  };

export const getAiTextForBrowserExtention =
  (extensionUrl, listingData) => async (dispatch: any, getState: any) => {
    const url = resolve('lomavis_ai_messages', {}, {});
    try {
      // update isWaitingForResponse

      const payload = {
        quick_action: 12,
        location: getState().lomavisCreator.locationUuid,
        additional_data: {
          language: 'de',
          url: extensionUrl,
          text: JSON.stringify(listingData)
        }
      };

      // get current tone settings and add to message
      const response = await axiosClient.post(url, payload);
      // get old messages list

      console.log('response AI', response.data);
      return response.data;
      // dispatch(updateAiAssistantMessages([...response.data, ...oldMessages]));
    } catch (err: any) {
      console.log(err);
    }
  };

// Meta Ads Targeting API functions
export const fetchMetaAdsTargeting =
  (type: 'interests' | 'countries' | 'cities' | 'gender', query?: string) =>
  async (dispatch: any, getState: any) => {
    try {
      const params: { type: string; query?: string } = { type };
      if (query && ['interests', 'countries', 'cities'].includes(type)) {
        params.query = query;
      }

      const url = resolve('meta_ads_targeting', {}, params);
      const response = await axiosClient.get(url);

      return {
        type,
        data: response.data.data || []
      };
    } catch (err: any) {
      console.error('Error fetching Meta Ads targeting:', err);
      return {
        type,
        data: []
      };
    }
  };

export const searchInstagramAudio =
  ({
    locationUuid,
    searchUrl,
    query,
    audioType,
    signal
  }: {
    locationUuid?: string;
    searchUrl?: string;
    query: string;
    audioType: 'music' | 'original_sound';
    signal?: AbortSignal;
  }) =>
  async () => {
    // Prefer the explicit URL coming from the backend creator config (it is
    // already scoped to the right location/IG account, including in multipost
    // mode where there is no current locationUuid). Fall back to building the
    // URL client-side from a locationUuid for backwards compatibility.
    const baseUrl = searchUrl
      ? searchUrl
      : locationUuid
      ? resolve('instagram_audio_search', { locationUuid }, {})
      : null;

    if (!baseUrl) {
      return [];
    }

    const params = new URLSearchParams({ q: query, type: audioType });
    const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params.toString()}`;

    const response = await axiosClient.get(url, { signal });
    return response.data?.results || [];
  };

export const fetchMetaAdsInterests =
  (query: string) => async (dispatch: any, getState: any) => {
    if (!query) {
      return { type: 'interests', data: [] };
    }
    return dispatch(fetchMetaAdsTargeting('interests', query));
  };

export const fetchMetaAdsCountries =
  (query: string) => async (dispatch: any, getState: any) => {
    if (!query) {
      return { type: 'countries', data: [] };
    }
    return dispatch(fetchMetaAdsTargeting('countries', query));
  };

export const fetchMetaAdsCities =
  (query: string) => async (dispatch: any, getState: any) => {
    if (!query) {
      return { type: 'cities', data: [] };
    }
    return dispatch(fetchMetaAdsTargeting('cities', query));
  };
